import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Storage from './utils/storageES.js'
import Challenges from './data/challengesES.js'
import { getCountryCode } from './utils/countryUtils.js'
import Splash from './components/Splash.jsx'
import Notes from './components/Notes.jsx'
import UsefulInfo from './components/UsefulInfo.jsx'
import Checklists from './components/Checklists.jsx'
import QuickMenu from './components/QuickMenu.jsx'
import Plans from './components/Plans.jsx'
import Explore from './components/Explore.jsx'
import TripSummary from './components/TripSummary.jsx'
import MyTrips from './components/MyTrips.jsx'
import Settings from './components/Settings.jsx'
import InstallPrompt from './components/InstallPrompt.jsx'
import AppUpdatePrompt from './components/AppUpdatePrompt.jsx'
import BackupPrompt from './components/BackupPrompt.jsx'
import AutoSaveIndicator from './components/AutoSaveIndicator.jsx'
import VersionFooter from './components/VersionFooter.jsx'
import AuthScreen from './components/AuthScreen.jsx'
import LanguageSwitcher from './components/LanguageSwitcher.jsx'
import ShareModal from './components/ShareModal.jsx'
import authManager from './utils/authManager.js'
import licenseManager from './utils/licenseManager.js'
import deepLinkManager from './utils/deepLinkManager.js'
import PhotoMigration from './utils/migratePhotos.js'
import persistentStorage from './utils/persistentStorage.js'
import DayDetailModal from './components/DayDetailModal.jsx'
import ProximityLayer from './components/ProximityLayer.jsx'
import PostcardLayer from './components/PostcardLayer.jsx'
import GemConstellation from './components/GemConstellation.jsx'
import PostcardTeaser from './components/PostcardTeaser.jsx'
import BackgroundTaskLayer from './components/BackgroundTaskLayer.jsx'
import autoBackupManager from './utils/autoBackupManager.js'
import { runTask, isRunning } from './utils/backgroundTasks.js'
import * as googleDrive from './utils/googleDrive.js'
import { FULL_FILE, noteFullBackupDone } from './utils/driveAutoBackup.js'
import GiftReceivedLayer from './components/GiftReceivedLayer.jsx'
import CloudBackupNudge from './components/CloudBackupNudge.jsx'
import PermissionsOnboarding from './components/PermissionsOnboarding.jsx'
import { getActivityStatus, getStatusColor } from './utils/activityStatus.js'
import AtlasView from './components/AtlasView.jsx'
import SubmitSpotForm from './components/SubmitSpotForm.jsx'
import { aggregateProfile, getProfile, checkPendingStatuses } from './utils/profileManager.js'
import { getLevel } from './data/badges.js'
import './styles/main-page.css'

// Helper function to normalize city names across languages
const normalizeCityName = (cityName) => {
  // Helper to remove accents and diacritics
  const removeAccents = (str) => {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  };

  const cityMap = {
    // Portuguese cities
    'lisbon': ['lisboa', 'lisbon', 'lisbonne'],
    'porto': ['oporto', 'porto', 'port'],
    // Italian cities
    'rome': ['roma', 'rome'],
    'milan': ['milano', 'milan'],
    'venice': ['venezia', 'venice', 'venecia'],
    'florence': ['firenze', 'florence', 'florencia'],
    'naples': ['napoli', 'naples'],
    'turin': ['torino', 'turin'],
    // Spanish cities
    'seville': ['sevilla', 'seville'],
    'cordoba': ['cordoba'],
    'zaragoza': ['saragossa', 'zaragoza'],
    // French cities
    'marseille': ['marseille', 'marseilles', 'marsella'],
    'nice': ['nice', 'niza'],
    'bordeaux': ['bordeaux', 'burdeos'],
    'strasbourg': ['strasbourg', 'estrasburgo'],
    'brussels': ['brussels', 'bruxelles', 'bruselas'],
    'bruges': ['bruges', 'brugge', 'brujas'],
    'antwerp': ['antwerp', 'antwerpen', 'amberes'],
    'vienna': ['vienna', 'wien', 'viena'],
    'salzburg': ['salzburg', 'salzburgo'],
    // Add more as needed
  };

  // First normalize: lowercase, trim, and remove accents
  const normalizedWithAccents = cityName.toLowerCase().trim();
  const normalized = removeAccents(normalizedWithAccents);

  // Find if this city matches any variant (check both with and without accents)
  for (const [key, variants] of Object.entries(cityMap)) {
    if (variants.some(v => removeAccents(v) === normalized || v === normalizedWithAccents)) {
      return key;
    }
  }

  return normalized;
};

// Home Component (simplified for now)
function Home({ userData, onNavigate, onMenuToggle, onChallengeSelect, refreshData, onShowPremiumChallengeModal, translatedCityName, onCityNameTranslated, onShowQuickMenu }) {
  const { t, i18n } = useTranslation();
  const [currentChallenge, setCurrentChallenge] = useState(null)
  const [showSpotForm, setShowSpotForm] = useState(false)
  const [tripStats, setTripStats] = useState({ photos: 0, notes: 0, challenges: 0, accomplishedChallenges: 0 })

  // Save trip → background backup task (uploads to Drive if connected, else the
  // completion chip offers share/download). Used by the fixed bottom bar.
  const handleSaveTrip = () => {
    if (isRunning('backup')) return
    runTask({
      id: 'backup', kind: 'backup', label: t('tasks.backupLabel'),
      run: async (onProgress) => {
        const backup = await autoBackupManager.createBackup(onProgress)
        if (googleDrive.isConnected()) {
          onProgress({ phase: t('tasks.uploadingDrive'), current: 0, total: 0 })
          try {
            await googleDrive.uploadBackup(backup, FULL_FILE)
            autoBackupManager.markBackedUp()
            noteFullBackupDone()
            return { kind: 'backup', uploaded: true, doneText: t('tasks.backupDrive') }
          } catch {
            return { kind: 'backup', backup, doneText: t('tasks.backupReady') }
          }
        }
        return { kind: 'backup', backup, doneText: t('tasks.backupReady') }
      },
    })
  }
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0)
  const [dailyReflections, setDailyReflections] = useState([])
  const [allPhotos, setAllPhotos] = useState([])
  const [marqueeSpeed, setMarqueeSpeed] = useState(60)
  const [timelineTooltip, setTimelineTooltip] = useState({ show: false, activity: null, day: null, period: null, x: 0, y: 0 })
  const [carouselIndex, setCarouselIndex] = useState(0)
  const [isCarouselPaused, setIsCarouselPaused] = useState(false)
  const [cityActivities, setCityActivities] = useState([])
  const [challengeIndex, setChallengeIndex] = useState(0)
  const [showPremiumChallengeModal, setShowPremiumChallengeModal] = useState(false)
  const [isChallengeCarouselPaused, setIsChallengeCarouselPaused] = useState(false)
  const [availableChallenges, setAvailableChallenges] = useState([])
  const [showScheduleModal, setShowScheduleModal] = useState(false)
  const [pendingChallenge, setPendingChallenge] = useState(null)
  const [scheduleDay, setScheduleDay] = useState(null)
  const [schedulePeriod, setSchedulePeriod] = useState('morning')
  const [showDayDetailModal, setShowDayDetailModal] = useState(false)
  const [selectedDetailDay, setSelectedDetailDay] = useState(null)
  const [timelineRefreshKey, setTimelineRefreshKey] = useState(0)
  const [profileSnapshot, setProfileSnapshot] = useState(null)
  const [communitySpots, setCommunitySpots] = useState([])
  const [loadingCommunitySpots, setLoadingCommunitySpots] = useState(false)
  const [cityHeaderImage, setCityHeaderImage] = useState(null)

  // Photo states for city activities
  const [activityPhotos, setActivityPhotos] = useState({})
  const [loadingActivityPhotos, setLoadingActivityPhotos] = useState({})
  const [budgetData, setBudgetData] = useState({
    dailyBudget: 0,
    spentToday: 0,
    totalSpent: 0,
    lastResetDate: new Date().toDateString(),
    categories: {
      food: 0,
      transport: 0,
      activities: 0,
      shopping: 0,
      accommodation: 0,
      other: 0
    }
  })
  
  // Touch/swipe gesture state
  const [touchStart, setTouchStart] = useState(null)
  const [touchEnd, setTouchEnd] = useState(null)

  // Minimum distance between touchStart and touchEnd to trigger a swipe
  const minSwipeDistance = 50

  // Photo fetching functions (copied from Plans.jsx)
  const getWikipediaLanguage = (country, city) => {
    const languageMap = {
      'Spain': 'es', 'España': 'es', 'France': 'fr', 'Italia': 'it', 'Italy': 'it',
      'Deutschland': 'de', 'Germany': 'de', 'Portugal': 'pt', 'Nederland': 'nl',
      'Greece': 'el', 'Grecia': 'el', 'Россия': 'ru', 'Russia': 'ru',
      'Polska': 'pl', 'Poland': 'pl', 'Česká republika': 'cs', 'Czech Republic': 'cs',
      'Norge': 'no', 'Norway': 'no', 'Sverige': 'sv', 'Sweden': 'sv',
      'Danmark': 'da', 'Denmark': 'da', 'Suomi': 'fi', 'Finland': 'fi',
      'Österreich': 'de', 'Austria': 'de', 'Schweiz': 'de', 'Switzerland': 'de',
      'België': 'nl', 'Belgium': 'nl', 'Belgique': 'fr', 'Irlande': 'en',
      'Ireland': 'en', 'Islande': 'is', 'Iceland': 'is', 'Magyarország': 'hu',
      'Hungary': 'hu', 'România': 'ro', 'Romania': 'ro', 'България': 'bg',
      'Bulgaria': 'bg', 'Hrvatska': 'hr', 'Croatia': 'hr', 'Srbija': 'sr',
      'Serbia': 'sr', 'Slovenija': 'sl', 'Slovenia': 'sl', 'Slovensko': 'sk',
      'Slovakia': 'sk', 'Lietuva': 'lt', 'Lithuania': 'lt', 'Latvija': 'lv',
      'Latvia': 'lv', 'Eesti': 'et', 'Estonia': 'et'
    };
    return languageMap[country] || 'en';
  };

  const isValidPhotoUrl = (url) => {
    if (!url) return false;
    const urlLower = url.toLowerCase();
    if (urlLower.includes('.svg')) {
      console.log(`🚫 Skipping SVG file: ${url}`);
      return false;
    }
    const excludedFormats = ['.gif', '.webm', '.mp4', '.pdf', '.doc'];
    if (excludedFormats.some(format => urlLower.includes(format))) {
      return false;
    }
    return true;
  };

  const fetchActivityPhoto = async (activity) => {
    if (!activity?.name || activityPhotos[activity.name] || loadingActivityPhotos[activity.name]) return;

    setLoadingActivityPhotos(prev => ({ ...prev, [activity.name]: true }));
    console.log('🏛️ [MAIN PAGE] Loading photo for activity: ' + activity.name);

    try {
      const country = userData?.country || 'Spain';
      const city = userData?.destination || '';
      const wikiLang = getWikipediaLanguage(country, city);

      // Create multiple search variations (same logic as Plans.jsx)
      const searchName = activity.localName || activity.name;
      // Keep accented characters - only remove special punctuation, not accents
      const cleanedName = searchName.replace(/[^\w\s\-\(\)àáâãäåæçèéêëìíîïñòóôõöøùúûüýÿÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÑÒÓÔÕÖØÙÚÛÜÝŸ]/g, '').trim();

      console.log('🔍 [MAIN PAGE] Original name: ' + searchName + ', cleaned: ' + cleanedName);

      const variations = [
        searchName,
        cleanedName,
        cleanedName.toLowerCase(),
        cleanedName.charAt(0).toUpperCase() + cleanedName.slice(1).toLowerCase(),
        cleanedName.split(' ').map(word =>
          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        ).join(' ')
      ];

      // Add city context if available
      if (city && city.toLowerCase() !== cleanedName.toLowerCase()) {
        variations.push(cleanedName + ' ' + city);
        variations.push(cleanedName + ', ' + city);
        variations.push(cleanedName + ' (' + city + ')');
      }

      // Remove duplicates and empty strings
      const uniqueVariations = [...new Set(variations)].filter(v => v && v.trim());
      console.log('🔍 [MAIN PAGE] Search variations for ' + activity.name + ':', uniqueVariations);

      // Try local Wikipedia first, then English as fallback (like Plans.jsx)
      const wikipediaLanguages = wikiLang !== 'en' ? [wikiLang, 'en'] : ['en'];

      for (const lang of wikipediaLanguages) {
        console.log('🔍 [MAIN PAGE] Searching ' + lang + '.wikipedia.org for ' + activity.name + '...');

        for (const searchTerm of uniqueVariations) {
          console.log('🔍 [MAIN PAGE] Trying ' + lang + ' search term: "' + searchTerm + '"');

          const wikiUrl = 'https://' + lang + '.wikipedia.org/w/api.php?action=query&format=json&origin=*&prop=pageimages|pageterms&titles=' + encodeURIComponent(searchTerm) + '&pithumbsize=400&piprop=thumbnail|name|original';

          try {
            const response = await fetch(wikiUrl);
            if (!response.ok) {
              console.log('🔍 [MAIN PAGE] HTTP error ' + response.status + ' for term: ' + searchTerm);
              continue;
            }

            const data = await response.json();
            const pages = data.query?.pages;
            if (!pages) {
              console.log('🔍 [MAIN PAGE] No pages found for term: ' + searchTerm);
              continue;
            }

            const pageId = Object.keys(pages)[0];
            const page = pages[pageId];

            // Check for page existence more robustly
            if (!page || pageId === '-1' || page.missing) {
              console.log('🔍 [MAIN PAGE] ' + lang + ' Wikipedia page not found for ' + searchTerm);
              continue;
            }

            let imageUrl = page.thumbnail?.source;
            let originalUrl = page.original?.source;

            if (imageUrl && isValidPhotoUrl(imageUrl)) {
              const photoData = {
                urls: { regular: imageUrl, original: originalUrl || imageUrl },
                src: { medium: imageUrl, large: originalUrl || imageUrl },
                alt_description: page.title || activity.name,
                user: { name: lang.toUpperCase() + ' Wikipedia' },
                wikipedia: true,
                language: lang
              };

              setActivityPhotos(prev => ({ ...prev, [activity.name]: photoData }));
              console.log('✅ [MAIN PAGE] Got ' + lang + ' Wikipedia photo for ' + activity.name + ' using term "' + searchTerm + '": ' + imageUrl);
              return;
            } else {
              console.log('🔍 [MAIN PAGE] No valid photo for ' + lang + ' term: ' + searchTerm);
            }
          } catch (error) {
            console.log('🔍 [MAIN PAGE] Error with ' + lang + ' search term "' + searchTerm + '":', error);
            continue;
          }

          // Add delay between requests
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }

      console.log('❌ [MAIN PAGE] No photo found for ' + activity.name + ' after trying all variations');
    } catch (error) {
      console.error('❌ [MAIN PAGE] Error in fetchActivityPhoto for ' + activity.name + ':', error);
    } finally {
      setLoadingActivityPhotos(prev => ({ ...prev, [activity.name]: false }));
    }
  };

  // Handle touch start
  const handleTouchStart = (e) => {
    setTouchEnd(null) // otherwise the swipe is fired even with usual touch events
    setTouchStart(e.targetTouches[0].clientX)
  }

  // Handle touch move
  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  // Handle touch end - detect swipe direction and trigger navigation
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return
    
    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > minSwipeDistance
    const isRightSwipe = distance < -minSwipeDistance

    if (allPhotos.length <= 1) return // No swiping needed if 0 or 1 photo

    if (isLeftSwipe) {
      // Swipe left = show next photo
      setCurrentPhotoIndex((prev) => (prev + 1) % allPhotos.length)
    }
    if (isRightSwipe) {
      // Swipe right = show previous photo  
      setCurrentPhotoIndex((prev) => (prev - 1 + allPhotos.length) % allPhotos.length)
    }
  }

  useEffect(() => {
    loadHomeData()
  }, [])

  useEffect(() => {
    if (refreshData) {
      loadHomeData()
    }
  }, [refreshData])

  // Reload challenges when language changes
  useEffect(() => {
    const reloadChallenges = async () => {
      await Challenges.load(i18n.language)
      const allChallenges = Challenges.getAll() || []
      setAvailableChallenges(allChallenges)
      const challenge = Challenges.getRandom([])
      setCurrentChallenge(challenge)
      console.log(`✅ Challenges reloaded for language: ${i18n.language}`)
    }
    reloadChallenges()
  }, [i18n.language])

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (timelineTooltip.show && !event.target.closest('[data-timeline-activity]') && !event.target.closest('[data-timeline-tooltip]')) {
        setTimelineTooltip({ show: false, activity: null, day: null, period: null, x: 0, y: 0 })
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [timelineTooltip.show])

  // Auto-scroll carousel every 15 seconds
  useEffect(() => {
    if (isCarouselPaused) return

    const autoScrollInterval = setInterval(() => {
      const carousel = document.querySelector('.custom-activities-carousel')
      if (carousel) {
        const cardWidth = 212 // 200px + 12px gap
        const maxIndex = 5 // 6 cards, 0-indexed
        setCarouselIndex(prev => {
          const nextIndex = prev >= maxIndex ? 0 : prev + 1
          const scrollPosition = nextIndex * cardWidth
          carousel.scrollTo({
            left: scrollPosition,
            behavior: 'smooth'
          })
          return nextIndex
        })
      }
    }, 15000) // 15 seconds

    return () => clearInterval(autoScrollInterval)
  }, [isCarouselPaused])

  // Auto-scroll challenge carousel every 20 seconds
  useEffect(() => {
    if (isChallengeCarouselPaused || availableChallenges.length <= 1) return

    const challengeAutoScrollInterval = setInterval(() => {
      const carousel = document.querySelector('.custom-challenges-carousel')
      if (carousel) {
        const cardWidth = 282 // 270px + 12px gap
        const maxIndex = availableChallenges.length - 1
        setChallengeIndex(prev => {
          const nextIndex = prev >= maxIndex ? 0 : prev + 1
          const scrollPosition = nextIndex * cardWidth
          carousel.scrollTo({
            left: scrollPosition,
            behavior: 'smooth'
          })
          return nextIndex
        })
      }
    }, 20000) // 20 seconds

    return () => clearInterval(challengeAutoScrollInterval)
  }, [isChallengeCarouselPaused, availableChallenges.length])

  // Load city-specific activities using same logic as Plans component
  useEffect(() => {
    const getCityActivities = async () => {
      try {
        // Use same logic as Plans component
        const city = userData?.destination || 'Paris'
        const country = userData?.country || 'France'
        const countryCode = await getCountryCode(country)

        console.log('🏙️ [MAIN PAGE] Loading activities for:', city, country, 'countryCode:', countryCode);

        if (!countryCode) {
          console.log('🏙️ [MAIN PAGE] No country code, using defaults');
          setCityActivities(getDefaultActivities())
          return
        }

        // Determine language folder based on current i18n language
        let langFolder = '';
        if (i18n.language === 'es' || i18n.language === 'es-ES') {
          langFolder = 'es/';
        } else if (i18n.language === 'fr' || i18n.language === 'fr-FR') {
          langFolder = 'fr/';
        } else {
          langFolder = 'en/';
        }

        const url = `./TRIP/${langFolder}${countryCode}.json`
        console.log('🏙️ [MAIN PAGE] Fetching from:', url, 'for language:', i18n.language);
        const response = await fetch(url)

        if (!response.ok) {
          console.log('🏙️ [MAIN PAGE] Failed to fetch country data, using defaults');
          setCityActivities(getDefaultActivities())
          return
        }

        const countryData = await response.json()
        console.log('🏙️ [MAIN PAGE] Country data loaded, cities:', countryData.cities?.length);

        // Normalize both the search city and the cities in the data
        const normalizedSearchCity = normalizeCityName(city);
        console.log('🏙️ [MAIN PAGE] Normalized search city:', normalizedSearchCity);

        const cityData = countryData.cities?.find(c => {
          const normalizedDataCity = normalizeCityName(c.city);
          console.log('🏙️ [MAIN PAGE] Comparing:', normalizedDataCity, 'with', normalizedSearchCity);
          return normalizedDataCity === normalizedSearchCity;
        })

        if (!cityData) {
          console.log('🏙️ [MAIN PAGE] City not found, using defaults');
          setCityActivities(getDefaultActivities())
          return
        }

        console.log('🏙️ [MAIN PAGE] Found city data for:', cityData.city);
        console.log('🏙️ [MAIN PAGE] culturalAttractions:', cityData.culturalAttractions?.length);
        console.log('🏙️ [MAIN PAGE] localActivities:', cityData.localActivities?.length);
        console.log('🏙️ [MAIN PAGE] typicalFoodsCity:', cityData.typicalFoodsCity?.length);
        console.log('🏙️ [MAIN PAGE] monuments:', cityData.monuments?.length);

        const activities = []
        let id = 1

        // Get cultural attractions (2 max)
        if (cityData.culturalAttractions) {
          console.log('🏛️ [MAIN PAGE] Adding cultural attractions:', cityData.culturalAttractions.slice(0, 2).map(a => a.name));
          cityData.culturalAttractions.slice(0, 2).forEach(attraction => {
            activities.push({
              id: id++,
              name: attraction.name,
              localName: attraction.localName, // Copy localName for better Wikipedia search
              description: attraction.description,
              category: 'cultural',
              image: '🏛️',
              mapsLink: attraction.mapsLink
            })
          })
        }

        // Get local activities (2 max)
        if (cityData.localActivities) {
          console.log('🎯 [MAIN PAGE] Adding local activities:', cityData.localActivities.slice(0, 2).map(a => a.name));
          cityData.localActivities.slice(0, 2).forEach(activity => {
            activities.push({
              id: id++,
              name: activity.name,
              localName: activity.localName, // Copy localName for better Wikipedia search
              description: activity.description,
              category: 'activities',
              image: '🎯',
              mapsLink: activity.mapsLink
            })
          })
        }

        // Get food experiences (1 max)
        if (cityData.typicalFoodsCity) {
          console.log('🍽️ [MAIN PAGE] Adding food:', cityData.typicalFoodsCity.slice(0, 1).map(a => a.name));
          cityData.typicalFoodsCity.slice(0, 1).forEach(food => {
            activities.push({
              id: id++,
              name: food.name,
              localName: food.localName, // Copy localName for better Wikipedia search
              description: food.description,
              category: 'food',
              image: '🍽️',
              mapsLink: food.mapsLink
            })
          })
        }

        // Get monuments (1 max)
        if (cityData.monuments) {
          console.log('🏰 [MAIN PAGE] Adding monuments:', cityData.monuments.slice(0, 1).map(a => a.name));
          cityData.monuments.slice(0, 1).forEach(monument => {
            activities.push({
              id: id++,
              name: monument.name,
              localName: monument.localName, // Copy localName for better Wikipedia search
              description: monument.description,
              category: 'monuments',
              image: '🏰',
              mapsLink: monument.mapsLink
            })
          })
        }

        console.log('🎯 [MAIN PAGE] Total activities created:', activities.length, activities.map(a => a.name));
        setCityActivities(activities.slice(0, 6)) // Limit to 6 activities
      } catch (error) {
        console.error('Error loading city activities:', error)
        setCityActivities(getDefaultActivities())
      }
    }

    const getDefaultActivities = () => {
      return [
        {
          id: 1,
          name: "City Exploration",
          description: "Discover the historic center and local landmarks",
          category: "cultural",
          image: "🏛️"
        },
        {
          id: 2,
          name: "Local Cuisine",
          description: "Taste traditional dishes and local specialties",
          category: "food",
          image: "🍽️"
        },
        {
          id: 3,
          name: "Cultural Sites",
          description: "Visit museums, monuments and historic places",
          category: "cultural",
          image: "🏰"
        },
        {
          id: 4,
          name: "Local Activities",
          description: "Experience unique activities and local tours",
          category: "activities",
          image: "🎯"
        },
        {
          id: 5,
          name: "Shopping & Markets",
          description: "Browse local markets and traditional shops",
          category: "shopping",
          image: "🛍️"
        },
        {
          id: 6,
          name: "Nature & Walks",
          description: "Explore parks, trails and natural attractions",
          category: "nature",
          image: "🌲"
        }
      ]
    }

    if (userData?.destination && userData?.country) {
      getCityActivities()
    } else {
      setCityActivities(getDefaultActivities())
    }
  }, [userData?.destination, userData?.country, i18n.language])

  // Load accepted community spots for the current city
  useEffect(() => {
    const fetchCommunitySpots = async () => {
      if (!userData?.country || !userData?.destination) return
      setLoadingCommunitySpots(true)
      try {
        const resp = await fetch(`./api/community/spots.php?country=${encodeURIComponent(userData.country)}&city=${encodeURIComponent(userData.destination)}`)
        if (!resp.ok) throw new Error('Failed')
        const data = await resp.json()
        if (data.success) setCommunitySpots(data.spots || [])
      } catch (e) {
        console.error('Community spots load error:', e)
      } finally {
        setLoadingCommunitySpots(false)
      }
    }
    fetchCommunitySpots()
  }, [userData?.country, userData?.destination])

  // Resolve city header image from TRIP/img/{country}/{country}-{city}.webp
  useEffect(() => {
    const resolveCityImage = async () => {
      if (!userData?.country || !userData?.destination) { setCityHeaderImage(null); return }
      const countryCode = await getCountryCode(userData.country)
      if (!countryCode) { setCityHeaderImage(null); return }
      const folder = countryCode.replace(/_/g, '-')
      const citySlug = userData.destination
        .toLowerCase()
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-')
      // Prefer WebP, fall back to PNG — onError on the img handles missing files
      setCityHeaderImage(`./TRIP/img/${folder}/${folder}-${citySlug}.webp`)
    }
    resolveCityImage()
  }, [userData?.country, userData?.destination])

  // Update translated city name when activities are loaded (for immediate language change)
  useEffect(() => {
    const updateCityName = async () => {
      if (!userData?.destination || !userData?.country) return;

      try {
        const city = userData.destination;
        const country = userData.country;
        const countryCode = await getCountryCode(country);

        if (!countryCode) return;

        // Determine language folder
        let langFolder = '';
        if (i18n.language === 'es' || i18n.language === 'es-ES') {
          langFolder = 'es/';
        } else if (i18n.language === 'fr' || i18n.language === 'fr-FR') {
          langFolder = 'fr/';
        } else {
          langFolder = 'en/';
        }

        const url = `./TRIP/${langFolder}${countryCode}.json`;
        const response = await fetch(url);

        if (response.ok) {
          const data = await response.json();
          const normalizedSearchCity = normalizeCityName(city);
          const cityData = data.cities?.find(c => {
            const normalizedDataCity = normalizeCityName(c.city);
            return normalizedDataCity === normalizedSearchCity;
          });

          // Update translated city name in parent
          if (cityData && cityData.city && onCityNameTranslated) {
            console.log('🏙️ [MAIN PAGE] Found translated city name:', cityData.city);
            onCityNameTranslated(cityData.city);
          }
        }
      } catch (error) {
        console.error('Error updating city name:', error);
      }
    };

    updateCityName();
  }, [i18n.language, userData?.destination, userData?.country]);

  // Load photos for city activities
  useEffect(() => {
    if (cityActivities.length > 0) {
      console.log('🖼️ [MAIN PAGE] Loading photos for city activities:', cityActivities.map(a => a.name));
      console.log('🖼️ [MAIN PAGE] Activity data:', cityActivities);
      cityActivities.forEach((activity, index) => {
        // Stagger requests to avoid overwhelming the API
        setTimeout(() => {
          console.log('🖼️ [MAIN PAGE] Starting photo fetch for:', activity.name, 'localName:', activity.localName);
          fetchActivityPhoto(activity);
        }, index * 500); // 500ms delay between requests
      });
    }
  }, [cityActivities])

  // Load and manage budget data with daily reset
  useEffect(() => {
    const loadBudgetData = () => {
      const savedBudget = Storage.get('budgetData', budgetData)
      const today = new Date().toDateString()

      // Check if we need to reset daily spending
      if (savedBudget.lastResetDate !== today) {
        // Reset daily spending but keep daily budget and total spent
        const resetBudget = {
          ...savedBudget,
          spentToday: 0,
          lastResetDate: today,
          categories: {
            food: 0,
            transport: 0,
            activities: 0,
            shopping: 0,
            accommodation: 0,
            other: 0
          }
        }
        setBudgetData(resetBudget)
        Storage.set('budgetData', resetBudget)
      } else {
        setBudgetData(savedBudget)
      }
    }

    loadBudgetData()
  }, [refreshData])

  const loadHomeData = async () => {
    try {
      console.log('[loadHomeData] Starting to load home data...');

      // Migrate existing photos from cache to IndexedDB (one-time operation)
      const migrationFlag = localStorage.getItem('photos_migrated_to_indexeddb');
      if (!migrationFlag) {
        console.log('[loadHomeData] 📸 Running photo migration...');
        const migrationResult = await PhotoMigration.migrateExistingPhotos();
        console.log('[loadHomeData] 📸 Migration result:', migrationResult);
        if (migrationResult.success) {
          localStorage.setItem('photos_migrated_to_indexeddb', 'true');
        }
      }

      // Use async version to ensure we get the latest trip data
      const tripData = await Storage.getTripDataAsync()
      console.log('[loadHomeData] Trip data loaded:', {
        notes: tripData.notes?.length,
        timeline: Object.keys(tripData.timeline || {}),
        challenges: tripData.challenges?.length
      });

      // Count challenge notes (accepted challenges)
      const challengeNotes = tripData.notes.filter(note => note.isChallenge === true) || []

      // Count accomplished challenges (those submitted for review)
      const accomplishedChallenges = challengeNotes.filter(note =>
        note.challengeData && note.challengeData.submitted === true
      ) || []

      setTripStats({
        photos: tripData.totalPhotoCount ?? tripData.photos?.length ?? 0,
        notes: tripData.notes?.length || 0,
        challenges: challengeNotes.length,
        accomplishedChallenges: accomplishedChallenges.length
      })

      // Photos are already limited to 20 most recent by storageManager (OOM prevention)
      setAllPhotos(tripData.photos || [])
      console.log('[loadHomeData] 📸 Carousel photos:', (tripData.photos || []).length, '/ total:', tripData.totalPhotoCount);

      // Load daily reflections from reflections data
      const reflections = tripData.reflections || []
      const reflectionTexts = []
      
      reflections.forEach(reflection => {
        if (reflection.answers) {
          // Extract non-empty answers and add them to the marquee
          Object.values(reflection.answers).forEach(answer => {
            if (answer && answer.trim().length > 0) {
              reflectionTexts.push(answer.trim())
            }
          })
        }
      })
      
      const displayReflections = reflectionTexts.slice(0, 10) // Show last 10 reflection answers
      setDailyReflections(displayReflections)
      
      // Calculate dynamic marquee speed based on content length
      const totalChars = displayReflections.join(' • ').length
      const baseSpeed = 60 // Base speed in seconds
      const speedMultiplier = Math.max(0.5, Math.min(3, totalChars / 200)) // Scale between 0.5x and 3x based on character count
      setMarqueeSpeed(Math.round(baseSpeed * speedMultiplier))
      
      await Challenges.load(i18n.language)
      // Load all challenges for carousel
      const allChallenges = Challenges.getAll() || []
      console.log('[loadHomeData] Loaded challenges:', allChallenges.length, 'challenges');

      // For now, just show all challenges (we'll filter later if needed)
      setAvailableChallenges(allChallenges)
      console.log('[loadHomeData] Set availableChallenges to:', allChallenges.length, 'challenges');

      // Keep the original single challenge for backward compatibility
      const challenge = Challenges.getRandom([])
      setCurrentChallenge(challenge)

      console.log('[loadHomeData] ✅ Home data loaded successfully');

      // Check server for challenge/spot status updates, then refresh profile snapshot
      checkPendingStatuses()
        .then(anyChange => anyChange ? aggregateProfile() : getProfile())
        .then(result => {
          const p = result?.profile ?? result
          if (p) setProfileSnapshot(p)
        })
        .catch(() => getProfile().then(p => { if (p) setProfileSnapshot(p) }))
    } catch (error) {
      console.error('Error loading home data:', error)
    }
  }

  const getDaysUntilTrip = () => {
    if (!userData || !userData.startDate) return null
    const today = new Date()
    const tripStart = new Date(userData.startDate)
    const diffTime = tripStart - today
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }

  const getTripDuration = () => {
    if (!userData || !userData.startDate || !userData.endDate) return null
    const start = new Date(userData.startDate)
    const end = new Date(userData.endDate)
    const diffTime = end - start
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }

  const daysUntilTrip = getDaysUntilTrip()
  const tripDuration = getTripDuration()

  const getCurrentTripDay = () => {
    if (!userData || !userData.startDate) return 1
    const today = new Date()
    const tripStart = new Date(userData.startDate)
    const diffTime = today - tripStart
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1
    return Math.max(1, diffDays)
  }

  const getTimelineActivities = () => {
    const tripData = Storage.getTripData()
    return tripData.timeline || {}
  }

  const getActivityColor = (category) => {
    const colors = {
      'food': '#E74C3C',           // Red - for food/restaurants
      'cultural': '#9B59B6',       // Purple - for museums/cultural sites
      'monuments': '#34495E',      // Dark blue-gray - for monuments/landmarks
      'activities': '#27AE60',     // Green - for activities/sports
      'locals': '#F39C12',         // Orange - for local experiences
      'shopping': '#E67E22',       // Orange-red - for shopping
      'nature': '#16A085',         // Teal - for nature/parks
      'nightlife': '#8E44AD',      // Dark purple - for nightlife
      'transport': '#3498DB',      // Blue - for transportation
      'challenge': '#FFD700',      // Gold - for challenges
      'default': '#D4A574'         // Default brown - fallback
    }
    return colors[category] || colors['default']
  }

  const handleActivityHover = (activity, event, day, period, show = true) => {
    if (show && activity) {
      const rect = event.target.getBoundingClientRect()
      setTimelineTooltip({
        show: true,
        activity: activity,
        day: day,
        period: period,
        x: rect.left + rect.width / 2,
        y: rect.top - 10
      })
    } else {
      setTimelineTooltip({ show: false, activity: null, day: null, period: null, x: 0, y: 0 })
    }
  }

  const handleActivityClick = (activity, event, day, period) => {
    if (activity) {
      // Show tooltip when clicking the activity square
      const rect = event.target.getBoundingClientRect()
      setTimelineTooltip({
        show: true,
        activity: activity,
        day: day,
        period: period,
        x: rect.left + rect.width / 2,
        y: rect.top - 10
      })
    }
  }

  const handleTooltipClick = (activity, day, period) => {
    if (activity) {
      // Navigate to notes page and go directly to the specific day
      setTimelineTooltip({ show: false, activity: null, day: null, period: null, x: 0, y: 0 }) // Hide tooltip
      onNavigate('notes', { directToBookView: true, selectedDay: day, activityId: activity.id })
    }
  }

  const handleAcceptChallenge = async (challenge) => {
    // Sprint 3 (PRIZE_GATE_MODE='claim'): accepting, scheduling, completing and
    // sharing are FREE for everyone — that's the word-of-mouth engine. The
    // premium gate (prize_challenges) now lives only at the prize-claim step
    // (the private submission for physical merch), handled in the journal.
    setPendingChallenge(challenge);
    setScheduleDay(getCurrentTripDay()); // Default to current day
    setSchedulePeriod('morning'); // Default to morning
    setShowScheduleModal(true);
  }

  const handleConfirmSchedule = async () => {
    if (!scheduleDay || !schedulePeriod) {
      alert('Please select when you want to do this challenge!');
      return;
    }

    const challenge = pendingChallenge;

    // Check if slot is full (max 3 activities)
    const tripData = Storage.getTripData();
    tripData.timeline = tripData.timeline || {};
    const timelineKey = `day${scheduleDay}_${schedulePeriod}`;
    const existingActivities = tripData.timeline[timelineKey] || [];

    if (existingActivities.length >= 3) {
      alert(`The ${schedulePeriod} of Day ${scheduleDay} is full! Please choose another time slot.`);
      return;
    }

    // Create a special challenge note entry
    const challengeNote = {
      id: Date.now().toString(),
      type: 'challenge',
      challengeId: challenge.id,
      title: `🎯 Challenge: ${challenge.title}`,
      content: `
**Challenge Instructions:**
${challenge.description}

**Difficulty Level:** ${challenge.difficulty.toUpperCase()}
**Points Reward:** ${challenge.points} points

**Scheduled:** Day ${scheduleDay} - ${schedulePeriod.toUpperCase()}

---

**🏆 HOW TO WIN A PRIZE:**

To complete this challenge and be eligible for a LOS DIAS prize:

1. **Complete the challenge** as described above
2. **Take a photo or video** of yourself doing the challenge
3. **Wear your LOS DIAS t-shirt** in the photo/video (this is required!)
4. **Share it on Instagram** and tag us
5. **Submit for review** using the button below

**Prize Details:**
- Complete this challenge wearing your LOS DIAS t-shirt
- Submit proof via photo/video on Instagram
- Winners receive exclusive LOS DIAS travel gear!

---

*Ready to claim your prize? Click "Submit Challenge" below!*
      `,
      photos: [],
      tags: ['challenge', 'prize', 'competition'],
      date: new Date().toISOString().split('T')[0],
      time: new Date().toLocaleTimeString('en-US', { hour12: false }),
      tripDay: scheduleDay, // Use selected day
      isChallenge: true,
      challengeData: challenge,
      scheduledDay: scheduleDay,
      scheduledPeriod: schedulePeriod
    }

    // Save note
    tripData.notes = tripData.notes || [];
    tripData.notes.push(challengeNote);

    // Add to timeline
    tripData.timeline[timelineKey] = tripData.timeline[timelineKey] || [];
    tripData.timeline[timelineKey].push({
      id: challengeNote.id,
      name: challenge.title,
      category: 'challenge',
      description: challenge.description
    });

    // Await the save so the IndexedDB write completes before we re-read below.
    // (The fire-and-forget Storage.saveTripData races with loadHomeData's async
    //  re-read, which would clobber the cache and drop the challenge.)
    await Storage.saveTripDataAsync(tripData);

    // Close modal
    setShowScheduleModal(false);
    setPendingChallenge(null);

    // Reload home data to update stats and timeline
    await loadHomeData();

    // Show confirmation
    alert(`✅ Challenge accepted and scheduled for Day ${scheduleDay} - ${schedulePeriod}!`);

    // Navigate to notes and open the chapter for the day it was scheduled to
    onNavigate('notes', { directToBookView: true, selectedDay: scheduleDay });
  }

  const getCurrentDate = () => {
    const today = new Date()
    const options = { weekday: 'long', month: 'short', day: 'numeric' }
    return today.toLocaleDateString('en-US', options)
  }

  const getCurrentDay = () => {
    if (!userData || !userData.startDate) return null
    const today = new Date()
    const tripStart = new Date(userData.startDate)
    const diffTime = today - tripStart
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1
    return diffDays > 0 ? diffDays : null
  }

  const currentDay = getCurrentDay()

  // console.log('userData:', userData)

  if (!userData) {
    return <div>Loading...</div>
  }

  return (
    <div className="container home-screen">
      {/* Menu Button and Branding */}
      <div style={{
        position: 'absolute',
        top: '20px',
        left: '20px',
        right: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        zIndex: 100
      }}>
        <button className="home-menu-button" onClick={() => onMenuToggle()} style={{
          position: 'relative',
          top: 'auto',
          left: 'auto'
        }}>
          ☰
        </button>
        <div style={{
          fontSize: '20px',
          fontWeight: '600',
          color: '#8B7355',
          textAlign: 'right'
        }}>
          <span style={{
            fontFamily: "'Noto Serif', serif",
            textTransform: 'uppercase',
            letterSpacing: '2px',
            fontSize: '18px',
            fontWeight: '700'
          }}>{t('app.name')}</span>
          <span style={{ fontSize: '14px', fontWeight: '400', marginLeft: '4px' }}>{t('app.byline')}</span>
        </div>
      </div>

      {/* Separator Line */}
      <div style={{
        position: 'absolute',
        top: '80px',
        left: '20px',
        right: '20px',
        height: '1px',
        background: 'linear-gradient(90deg, transparent 0%, #B8A082 20%, #B8A082 80%, transparent 100%)',
        opacity: '0.3',
        zIndex: 99
      }}></div>

      {/* Top Header Section - Like Mockup */}
      <div className="top-header">
        {cityHeaderImage && (
          <img
            src={cityHeaderImage}
            alt=""
            onError={(e) => {
              // WebP missing — try PNG fallback, then give up
              if (e.target.src.endsWith('.webp')) {
                e.target.src = e.target.src.replace('.webp', '.png')
              } else {
                e.target.style.display = 'none'
              }
            }}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'bottom',
              opacity: 0.2,
              borderRadius: '0 0 20px 20px',
              pointerEvents: 'none',
              zIndex: 0
            }}
          />
        )}
        <div className="greeting-section">
          <h2 className="greeting">{t('home.greeting', { name: userData.userName || 'User' })}</h2>
          <h1 className="destination-title">{(translatedCityName || userData.destination || 'CITY').toUpperCase()}</h1>
          <p className="country-name">{(userData.country || 'COUNTRY').toUpperCase()}</p>
          <div className="useful-info-section">
            <button className="useful-info-btn" onClick={() => onNavigate('info')}>{t('home.usefulInfo')}</button>
          </div>
          <p className="current-date">{getCurrentDate()}</p>
        </div>

        <div className="weather-trip-section">
          <div className="weather-today">
            <span className="weather-icon">☀️</span>
            <span className="temperature">22°C</span>
          </div>

          <div className="weather-tomorrow">
                        <div className="tomorrow-info">
              <span className="tomorrow-label">{t('home.tomorrow')}</span>
              <span className="tomorrow-temp">18°C</span>
            </div>
            <span className="weather-icon">🌤️</span>

          </div>

          {currentDay && tripDuration && (
            <div className="trip-progress">
              <span className="day-counter">{t('home.dayProgress', { current: currentDay, total: tripDuration })}</span>
            </div>
          )}
        </div>
      </div>

      {/* Separator Line */}
      <div style={{
        height: '1px',
        background: 'rgba(184, 160, 130, 0.2)',
        margin: '8px 20px'
      }}></div>

      {/* Sprint 4A — the city's secret constellation (tells how many gems exist,
          never where; lights as they're found). Hidden for cities with no gems. */}
      <div style={{ margin: '0 20px' }}>
        <GemConstellation country={userData.country} city={userData.destination} />
        {/* Sprint 4B — earn-or-buy the trip city's postcard (sober accordion). */}
        <div style={{ marginTop: '12px' }}>
          <PostcardTeaser country={userData.country} city={userData.destination} />
        </div>
      </div>

      {/* Submit Spot Modal (community contribution) */}
      {showSpotForm && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 200,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end',
        }} onClick={e => { if (e.target === e.currentTarget) setShowSpotForm(false) }}>
          <div style={{ background: 'white', borderRadius: '24px 24px 0 0', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <SubmitSpotForm
              userData={userData}
              onClose={() => setShowSpotForm(false)}
              onSubmitted={() => setShowSpotForm(false)}
            />
          </div>
        </div>
      )}

      {/* Trip Stats - Simplified */}
      <h3 className="section-title">{t('home.memoriesCreated')}</h3>
      <div className="custom-stats-horizontal-container" style={{
        display: 'flex', 
        flexDirection: 'row', 
        justifyContent: 'space-around', 
        alignItems: 'center',
        background: 'white',
        margin: '0 20px 20px 20px',
        padding: '20px',
        borderRadius: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        
        <div className="custom-stat-item-horizontal" style={{
          textAlign: 'center', 
          flex: '1',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <span style={{display: 'block', fontSize: '28px', fontWeight: '700', color: '#B8A082', marginBottom: '4px'}}>{tripStats.photos}</span>
          <span style={{display: 'block', fontSize: '12px', color: '#8B7355', textTransform: 'uppercase', letterSpacing: '0.5px'}}>{t('home.photos')}</span>
        </div>
        <div className="custom-stat-item-horizontal" style={{
          textAlign: 'center',
          flex: '1',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          cursor: 'pointer',
          transition: 'transform 0.2s ease'
        }} onClick={() => onNavigate('notes')}
        onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
        onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}>
          <span style={{display: 'block', fontSize: '28px', fontWeight: '700', color: '#B8A082', marginBottom: '4px'}}>{tripStats.notes}</span>
          <span style={{display: 'block', fontSize: '12px', color: '#8B7355', textTransform: 'uppercase', letterSpacing: '0.5px'}}>{t('home.notes')}</span>
        </div>
        <div className="custom-stat-item-horizontal" style={{
          textAlign: 'center',
          flex: '1',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <span style={{display: 'block', fontSize: '28px', fontWeight: '700', color: '#B8A082', marginBottom: '4px'}}>
            {tripStats.accomplishedChallenges}/{tripStats.challenges}
          </span>
          <span style={{display: 'block', fontSize: '12px', color: '#8B7355', textTransform: 'uppercase', letterSpacing: '0.5px'}}>{t('home.challenges')}</span>
        </div>
      </div>

      {/* Daily Reflections Marquee */}
      {dailyReflections.length > 0 && (
        <div style={{
          background: '#f8f6f0',
          margin: '0 20px 10px 20px',
          borderRadius: '12px',
          padding: '12px 0',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          position: 'relative',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            display: 'inline-block',
            animation: `marquee ${marqueeSpeed}s linear infinite`,
            fontSize: '14px',
            color: '#8B7355',
            paddingLeft: '100%'
          }}>
            {dailyReflections.join(' • ')}
          </div>
        </div>
      )}

      {/* Separator Line */}
      <div style={{
        height: '1px',
        background: 'rgba(184, 160, 130, 0.2)',
        margin: '8px 20px'
      }}></div>

      {/* Latest Photos Section */}
      <div className="photos-section">
        <h3 className="section-title">{t('home.latestPhotos')}</h3>
        <div className="photos-container"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}>
          {(() => {
            if (allPhotos.length === 0) {
              return (
                <>
                  <div className="side-photo"></div>
                  <div className="photos-mockup">
                    <div className="mockup-label">{t('home.noPhotos')}</div>
                  </div>
                  <div className="side-photo"></div>
                </>
              )
            }
            
            // Get current set of 3 photos starting from currentPhotoIndex
            const visiblePhotos = []
            for (let i = 0; i < 3; i++) {
              const index = (currentPhotoIndex + i) % allPhotos.length
              visiblePhotos.push(allPhotos[index])
            }
            
            const [leftPhoto, mainPhoto, rightPhoto] = visiblePhotos
            
            return (
              <>
                {/* Previous Button */}
                <button
                  onClick={() => setCurrentPhotoIndex((prev) => (prev - 1 + allPhotos.length) % allPhotos.length)}
                  style={{
                    position: 'absolute',
                    left: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgba(184, 160, 130, 0.8)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    fontSize: '16px',
                    cursor: 'pointer',
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  ←
                </button>

                {leftPhoto && (
                  <div className="side-photo" style={{
                    backgroundImage: `url(${leftPhoto.data})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    cursor: 'pointer'
                  }} onClick={() => setCurrentPhotoIndex((prev) => (prev - 1 + allPhotos.length) % allPhotos.length)}></div>
                )}
                
                <div className="photos-mockup" style={{
                  backgroundImage: `url(${mainPhoto.data})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  display: 'flex',
                  alignItems: 'flex-end',
                  padding: '16px',
                  cursor: 'pointer'
                }} onClick={() => {
                  // Open photo in full view
                  const newWindow = window.open()
                  newWindow.document.write(`
                    <img src="${mainPhoto.data}" style="max-width: 100%; max-height: 100vh; object-fit: contain;" />
                  `)
                }}>
                  {mainPhoto.memoryTitle && (
                    <div style={{
                      background: 'rgba(0,0,0,0.7)',
                      color: 'white',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      fontSize: '12px',
                      fontWeight: '600',
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {mainPhoto.memoryTitle}
                    </div>
                  )}
                </div>
                
                {rightPhoto && (
                  <div className="side-photo" style={{
                    backgroundImage: `url(${rightPhoto.data})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    cursor: 'pointer'
                  }} onClick={() => setCurrentPhotoIndex((prev) => (prev + 1) % allPhotos.length)}></div>
                )}

                {/* Next Button */}
                <button
                  onClick={() => setCurrentPhotoIndex((prev) => (prev + 1) % allPhotos.length)}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgba(184, 160, 130, 0.8)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    fontSize: '16px',
                    cursor: 'pointer',
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  →
                </button>
              </>
            )
          })()}
        </div>
        <div className="pagination-dots">
          {(() => {
            if (allPhotos.length === 0) return null
            
            const totalPages = Math.ceil(allPhotos.length / 3)
            const currentPage = Math.floor(currentPhotoIndex / 3)
            
            return Array.from({ length: Math.min(totalPages, 5) }, (_, i) => (
              <div 
                key={i}
                className={`dot ${i === currentPage % 5 ? 'active' : ''}`}
                style={{ cursor: 'pointer' }}
                onClick={() => setCurrentPhotoIndex(i * 3)}
              ></div>
            ))
          })()}
        </div>
      </div>

      {/* Separator Line */}
      <div style={{
        height: '1px',
        background: 'rgba(184, 160, 130, 0.2)',
        margin: '8px 20px'
      }}></div>

      {/* Budget Widget */}
      <div className="budget-widget" style={{
        background: 'white',
        borderRadius: '16px',
        padding: '16px',
        margin: '0 20px 20px 20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: `2px solid ${budgetData.spentToday > budgetData.dailyBudget ? '#E74C3C' : '#B8A082'}`
      }}>
        <div style={{
          textAlign: 'center',
          marginBottom: '12px'
        }}>
          <span style={{
            fontSize: '12px',
            fontWeight: '600',
            color: '#8B7355',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}>
            {t('mainPage.todaysBudget')}
          </span>
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center',
          marginBottom: budgetData.dailyBudget > 0 ? '12px' : '8px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: '20px',
              fontWeight: '700',
              color: budgetData.spentToday > budgetData.dailyBudget ? '#E74C3C' : '#8B7355'
            }}>
              €{budgetData.spentToday.toFixed(0)}
            </div>
            <div style={{
              fontSize: '11px',
              color: '#666',
              fontWeight: '500',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              {t('mainPage.spentToday')}
            </div>
          </div>

          {budgetData.dailyBudget > 0 && (
            <div style={{
              width: '1px',
              height: '40px',
              background: 'rgba(184, 160, 130, 0.3)'
            }}></div>
          )}

          {budgetData.dailyBudget > 0 && (
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#8B7355'
              }}>
                €{budgetData.dailyBudget.toFixed(0)}
              </div>
              <div style={{
                fontSize: '11px',
                color: '#666',
                fontWeight: '500',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                {t('mainPage.dailyBudget')}
              </div>
            </div>
          )}

          {budgetData.dailyBudget > 0 && (
            <>
              <div style={{
                width: '1px',
                height: '40px',
                background: 'rgba(184, 160, 130, 0.3)'
              }}></div>

              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: budgetData.spentToday > budgetData.dailyBudget ? '#E74C3C' : '#27AE60'
                }}>
                  €{Math.max(0, budgetData.dailyBudget - budgetData.spentToday).toFixed(0)}
                </div>
                <div style={{
                  fontSize: '11px',
                  color: '#666',
                  fontWeight: '500',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {t('mainPage.remaining')}
                </div>
              </div>
            </>
          )}
        </div>

        {budgetData.dailyBudget > 0 && (
          <div style={{
            background: '#f0f0f0',
            borderRadius: '6px',
            height: '8px',
            overflow: 'hidden',
            marginBottom: '8px'
          }}>
            <div style={{
              background: budgetData.spentToday > budgetData.dailyBudget ? '#E74C3C' : '#C4714A',
              height: '100%',
              width: `${Math.min(100, (budgetData.spentToday / budgetData.dailyBudget) * 100)}%`,
              borderRadius: '6px',
              transition: 'width 0.3s ease'
            }}></div>
          </div>
        )}

        {budgetData.dailyBudget === 0 && (
          <div style={{
            textAlign: 'center',
            marginTop: '8px'
          }}>
            <button
              onClick={() => onNavigate('plans', { tab: 'budget' })}
              style={{
                background: 'transparent',
                border: '2px solid #C4714A',
                color: '#C4714A',
                padding: '8px 16px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#C4714A'
                e.target.style.color = 'white'
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent'
                e.target.style.color = '#C4714A'
              }}
            >
              {t('mainPage.setDailyBudget')}
            </button>
          </div>
        )}

        {budgetData.dailyBudget > 0 && budgetData.spentToday > budgetData.dailyBudget && (
          <div style={{
            textAlign: 'center',
            marginTop: '4px'
          }}>
            <span style={{
              fontSize: '11px',
              color: '#E74C3C',
              fontWeight: '600'
            }}>
              ⚠️ {t('mainPage.overBudgetBy')} €{(budgetData.spentToday - budgetData.dailyBudget).toFixed(0)}
            </span>
          </div>
        )}
      </div>

      {/* Separator Line after Budget Widget */}
      <div style={{
        height: '1px',
        background: 'rgba(184, 160, 130, 0.3)',
        margin: '12px 20px 20px 20px'
      }}></div>

      {/* Trip Timeline Section */}
      <div key={timelineRefreshKey} className="custom-timeline-container" style={{margin: '0 20px 20px 20px'}}>
        <h3 style={{fontSize: '12px', color: '#B8A082', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '16px', textAlign: 'center'}}>{t('mainPage.tripTimeline')}</h3>

        {/* Activity Info Area */}
        <div
          className="custom-timeline-tooltip"
          data-timeline-tooltip
          style={{
            background: 'white',
            borderRadius: '20px',
            padding: '12px 20px',
            marginBottom: '16px',
            textAlign: 'center',
            fontSize: '12px',
            color: '#8B7355',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            minHeight: '44px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: timelineTooltip.show && timelineTooltip.activity ? 'pointer' : 'default',
            transition: 'all 0.2s ease'
          }}
          onClick={() => {
            if (timelineTooltip.show && timelineTooltip.activity) {
              handleTooltipClick(timelineTooltip.activity, timelineTooltip.day, timelineTooltip.period);
            }
          }}
          onMouseEnter={(e) => {
            if (timelineTooltip.show && timelineTooltip.activity) {
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }
          }}
          onMouseLeave={(e) => {
            if (timelineTooltip.show && timelineTooltip.activity) {
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
              e.currentTarget.style.transform = 'translateY(0)';
            }
          }}
        >
          {timelineTooltip.show && timelineTooltip.activity ? (
            <div style={{width: '100%'}}>
              <div style={{fontSize: '13px', fontWeight: '700', color: '#333', marginBottom: '4px'}}>
                {timelineTooltip.activity.name}
              </div>
              <div style={{fontSize: '11px', color: '#8B7355'}}>
                {timelineTooltip.activity.category?.toUpperCase()} • {t('mainPage.day')} {timelineTooltip.day} • {timelineTooltip.period === 'morning' ? `🌅 ${t('mainPage.morning')}` : `🌆 ${t('mainPage.afternoon')}`}
              </div>
              <div style={{fontSize: '9px', color: '#B8A082', marginTop: '4px', fontStyle: 'italic'}}>
                {t('mainPage.clickToView')}
              </div>
            </div>
          ) : (
            t('mainPage.hoverActivity')
          )}
        </div>

        {/* Week-based Timeline */}
        {(() => {
          const getWeekStructure = () => {
            if (!userData?.startDate || !userData?.endDate) return [];

            const startDate = new Date(userData.startDate);
            const endDate = new Date(userData.endDate);

            // Find first Monday before or on trip start
            const firstMonday = new Date(startDate);
            const dayOfWeek = firstMonday.getDay();
            const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
            firstMonday.setDate(firstMonday.getDate() - daysToSubtract);

            // Find last Sunday after or on trip end
            const lastSunday = new Date(endDate);
            const endDayOfWeek = lastSunday.getDay();
            const daysToAdd = endDayOfWeek === 0 ? 0 : 7 - endDayOfWeek;
            lastSunday.setDate(lastSunday.getDate() + daysToAdd);

            // Build weeks array (only weeks containing trip days)
            const weeks = [];
            const currentWeekStart = new Date(firstMonday);

            while (currentWeekStart <= lastSunday) {
              const weekEnd = new Date(currentWeekStart);
              weekEnd.setDate(weekEnd.getDate() + 6);

              // Check if this week contains any trip days
              const hasTrip = !(weekEnd < startDate || currentWeekStart > endDate);

              if (hasTrip) {
                const week = {
                  weekNumber: weeks.length + 1,
                  startDate: new Date(currentWeekStart),
                  endDate: new Date(weekEnd),
                  days: []
                };

                // Build 7 days for this week
                for (let i = 0; i < 7; i++) {
                  const dayDate = new Date(currentWeekStart);
                  dayDate.setDate(dayDate.getDate() + i);

                  // Calculate if this day is within trip
                  const isInTrip = dayDate >= startDate && dayDate <= endDate;
                  const tripDay = isInTrip
                    ? Math.floor((dayDate - startDate) / (1000 * 60 * 60 * 24)) + 1
                    : null;

                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const checkDate = new Date(dayDate);
                  checkDate.setHours(0, 0, 0, 0);

                  week.days.push({
                    date: dayDate,
                    dayOfWeek: [t('mainPage.daysOfWeek.MON'), t('mainPage.daysOfWeek.TUE'), t('mainPage.daysOfWeek.WED'), t('mainPage.daysOfWeek.THU'), t('mainPage.daysOfWeek.FRI'), t('mainPage.daysOfWeek.SAT'), t('mainPage.daysOfWeek.SUN')][i],
                    tripDay: tripDay,
                    isInTrip: isInTrip,
                    isToday: checkDate.getTime() === today.getTime()
                  });
                }

                weeks.push(week);
              }

              currentWeekStart.setDate(currentWeekStart.getDate() + 7);
            }

            return weeks;
          };

          const weeks = getWeekStructure();
          const timelineActivities = getTimelineActivities();

          return weeks.map((week, weekIndex) => (
            <div key={weekIndex} style={{marginBottom: '24px'}}>
              {/* Week Header */}
              <div style={{
                fontSize: '11px',
                fontWeight: '700',
                color: '#B8A082',
                marginBottom: '8px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                {t('mainPage.week')} {week.weekNumber}: {week.startDate.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })} - {week.endDate.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })}
              </div>

              {/* Week Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: '0',
                background: 'white',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                {week.days.map((day, dayIndex) => {
                  const morningActivities = day.isInTrip ? (timelineActivities[`day${day.tripDay}_morning`] || []) : [];
                  const afternoonActivities = day.isInTrip ? (timelineActivities[`day${day.tripDay}_afternoon`] || []) : [];

                  return (
                    <div key={dayIndex} style={{
                      borderRight: dayIndex < 6 ? '1px solid rgba(0,0,0,0.05)' : 'none',
                      padding: '8px 4px',
                      background: day.isToday
                        ? 'rgb(201 137 57 / 50%)'
                        : day.isInTrip
                          ? 'rgb(225 147 50 / 21%)'
                          : 'rgba(0, 0, 0, 0.03)',
                      opacity: day.isInTrip ? '1' : '0.5',
                      cursor: day.isInTrip ? 'pointer' : 'default'
                    }}
                    onClick={(e) => {
                      if (e.target.closest('[data-timeline-activity]')) return
                      if (day.isInTrip) {
                        setSelectedDetailDay(day.tripDay)
                        setShowDayDetailModal(true)
                      }
                    }}>
                      {/* Day Header */}
                      <div style={{textAlign: 'center', marginBottom: '8px'}}>
                        <div style={{fontSize: '9px', fontWeight: '700', color: '#8B7355', letterSpacing: '0.5px'}}>
                          {day.dayOfWeek}
                        </div>
                        <div style={{fontSize: '11px', color: '#333', fontWeight: '600'}}>
                          {day.date.toLocaleDateString('en-US', { day: 'numeric' })}
                        </div>
                        {day.isInTrip && (
                          <div style={{fontSize: '8px', color: '#B8A082', fontWeight: '700'}}>
                            Day {day.tripDay}
                          </div>
                        )}
                      </div>

                      {/* Activities */}
                      {day.isInTrip && (
                        <>
                          {/* Morning Activities */}
                          <div style={{display: 'flex', gap: '3px', justifyContent: 'center', marginBottom: '6px'}}>
                            {[0, 1, 2].map(slotIndex => {
                              const activity = morningActivities[slotIndex];
                              return (
                                <div
                                  key={`morning-${slotIndex}`}
                                  data-timeline-activity
                                  style={{
                                    width: '12px',
                                    height: '12px',
                                    background: activity ? getActivityColor(activity.category) : 'rgba(0,0,0,0.08)',
                                    borderRadius: '2px',
                                    cursor: activity ? 'pointer' : 'default',
                                    border: activity ? `1px solid ${getActivityColor(activity.category)}` : '1px dashed rgba(0,0,0,0.15)',
                                    transition: 'transform 0.2s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    if (activity) {
                                      e.currentTarget.style.transform = 'scale(1.3)';
                                      setTimelineTooltip({
                                        show: true,
                                        activity: activity,
                                        day: day.tripDay,
                                        period: 'morning',
                                        x: 0,
                                        y: 0
                                      });
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (activity) {
                                      e.currentTarget.style.transform = 'scale(1)';
                                    }
                                  }}
                                  onClick={activity ? (e) => handleActivityClick(activity, e, day.tripDay, 'morning') : undefined}
                                />
                              );
                            })}
                          </div>

                          {/* Afternoon Activities */}
                          <div style={{display: 'flex', gap: '3px', justifyContent: 'center'}}>
                            {[0, 1, 2].map(slotIndex => {
                              const activity = afternoonActivities[slotIndex];
                              return (
                                <div
                                  key={`afternoon-${slotIndex}`}
                                  data-timeline-activity
                                  style={{
                                    width: '12px',
                                    height: '12px',
                                    background: activity ? getActivityColor(activity.category) : 'rgba(0,0,0,0.08)',
                                    borderRadius: '2px',
                                    cursor: activity ? 'pointer' : 'default',
                                    border: activity ? `1px solid ${getActivityColor(activity.category)}` : '1px dashed rgba(0,0,0,0.15)',
                                    transition: 'transform 0.2s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    if (activity) {
                                      e.currentTarget.style.transform = 'scale(1.3)';
                                      setTimelineTooltip({
                                        show: true,
                                        activity: activity,
                                        day: day.tripDay,
                                        period: 'afternoon',
                                        x: 0,
                                        y: 0
                                      });
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (activity) {
                                      e.currentTarget.style.transform = 'scale(1)';
                                    }
                                  }}
                                  onClick={activity ? (e) => handleActivityClick(activity, e, day.tripDay, 'afternoon') : undefined}
                                />
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ));
        })()}
      </div>

      {/* Separator Line */}
      <div style={{
        height: '1px',
        background: 'rgba(184, 160, 130, 0.2)',
        margin: '8px 20px'
      }}></div>

      {/* City Activities Carousel */}
      <div className="custom-city-activities-container" style={{margin: '0 20px 20px 20px'}}>
        <h3 style={{
          fontSize: '12px',
          color: '#B8A082',
          fontWeight: '600',
          letterSpacing: '1px',
          textTransform: 'uppercase',
          marginBottom: '16px',
          textAlign: 'center'
        }}>
          {t('mainPage.whatToDoIn')} {userData?.destination?.toUpperCase() || t('mainPage.yourCity')}
        </h3>
        <div
          className="custom-activities-carousel"
          style={{
            display: 'flex',
            gap: '12px',
            overflowX: 'auto',
            scrollBehavior: 'smooth',
            paddingBottom: '8px'
          }}
          onMouseEnter={() => setIsCarouselPaused(true)}
          onMouseLeave={() => setIsCarouselPaused(false)}
          onTouchStart={(e) => {
            setIsCarouselPaused(true)
            e.currentTarget.startX = e.touches[0].clientX
            e.currentTarget.scrollStart = e.currentTarget.scrollLeft
          }}
          onTouchMove={(e) => {
            if (e.currentTarget.startX) {
              const diff = e.currentTarget.startX - e.touches[0].clientX
              e.currentTarget.scrollLeft = e.currentTarget.scrollStart + diff
            }
          }}
          onTouchEnd={(e) => {
            e.currentTarget.startX = null
            // Resume auto-scroll after 3 seconds of inactivity
            setTimeout(() => setIsCarouselPaused(false), 3000)
          }}
          onScroll={() => {
            // Pause auto-scroll when user manually scrolls
            setIsCarouselPaused(true)
            clearTimeout(window.carouselResumeTimeout)
            window.carouselResumeTimeout = setTimeout(() => setIsCarouselPaused(false), 3000)
          }}
        >
          {cityActivities.map(activity => (
              <div
                key={activity.id}
                className="activity-card"
                style={{
                  minWidth: '150px', // Reducido de 200px a 150px (25% menos)
                  maxWidth: '160px', // Añadido maxWidth para mayor control
                  background: 'white',
                  borderRadius: '12px',
                  padding: '12px', // Reducido padding de 16px a 12px
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  border: `2px solid ${getActivityColor(activity.category)}`
                }}
                onClick={() => onNavigate('plans', { tab: 'inspiration', activityName: activity.name })}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)'
                  e.target.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)'
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)'
                  e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)'
                }}
              >
                <div style={{
                  height: '100px', // Reducido de 120px a 100px para mejor proporción
                  width: '100%',
                  marginBottom: '8px',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  backgroundColor: '#f5f5f5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {activityPhotos[activity.name] ? (
                    <img
                      src={activityPhotos[activity.name].urls.regular}
                      alt={activity.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        // Fallback to emoji if image fails to load
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : loadingActivityPhotos[activity.name] ? (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      color: '#666'
                    }}>
                      Loading...
                    </div>
                  ) : null}
                  <div style={{
                    fontSize: '32px',
                    textAlign: 'center',
                    display: activityPhotos[activity.name] ? 'none' : 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%'
                  }}>
                    {activity.image}
                  </div>
                </div>
                <h4 style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#333',
                  margin: '0 0 6px 0',
                  textAlign: 'center'
                }}>
                  {activity.name}
                </h4>
                <p style={{
                  fontSize: '12px',
                  color: '#666',
                  margin: '0',
                  textAlign: 'center',
                  lineHeight: '1.3'
                }}>
                  {activity.description}
                </p>
                <div style={{
                  marginTop: '8px',
                  textAlign: 'center'
                }}>
                  <span style={{
                    fontSize: '10px',
                    color: getActivityColor(activity.category),
                    textTransform: 'uppercase',
                    fontWeight: '600',
                    letterSpacing: '0.5px'
                  }}>
                    {t(`mainPage.activityCategories.${activity.category}`)}
                  </span>
                </div>
              </div>
            ))}
        </div>
        <div style={{
          textAlign: 'center',
          marginTop: '12px'
        }}>
          <button
            style={{
              background: 'transparent',
              color: '#B8A082',
              border: '1px solid #B8A082',
              padding: '8px 16px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onClick={() => onNavigate('plans')}
            onMouseEnter={(e) => {
              e.target.style.background = '#C4714A'
              e.target.style.color = 'white'
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'transparent'
              e.target.style.color = '#B8A082'
            }}
          >
            {t('mainPage.viewAllActivities')}
          </button>
        </div>
      </div>

      {/* Contribute a hidden corner to the community (moved here from the retired profile) */}
      <button
        type="button"
        onClick={() => setShowSpotForm(true)}
        style={{
          display: 'block', width: 'calc(100% - 40px)', margin: '0 20px 6px',
          padding: '12px', borderRadius: '12px', border: '1px dashed #D4C5B0',
          background: 'transparent', color: '#8B7355', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
        }}
      >
        📍 {t('community.submitBtn')}
      </button>
      <p style={{ margin: '0 20px 20px', fontSize: '12px', lineHeight: 1.5, color: '#B8A082', textAlign: 'center' }}>
        {t('community.pitch')} <span style={{ color: '#A85B38', fontWeight: 700 }}>🎁 {t('community.reward')}</span>
      </p>

      {/* Community Spots */}
      {communitySpots.length > 0 && (
        <>
          <div style={{ height: '1px', background: 'rgba(184, 160, 130, 0.2)', margin: '8px 20px' }}></div>
          <div style={{ margin: '0 20px 20px 20px' }}>
            <h3 style={{
              fontSize: '12px',
              color: '#B8A082',
              fontWeight: '600',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              marginBottom: '16px',
              textAlign: 'center'
            }}>
              {t('mainPage.communitySpots')} {userData?.destination?.toUpperCase() || t('mainPage.yourCity')}
            </h3>
            <div style={{
              display: 'flex',
              gap: '12px',
              overflowX: 'auto',
              scrollBehavior: 'smooth',
              paddingBottom: '8px'
            }}>
              {communitySpots.map(spot => {
                const categoryEmoji = {
                  cultural: '🏛️', monuments: '🗿', activities: '🎯',
                  locals: '🏠', food: '🍽️'
                }[spot.category] || '📍'
                return (
                  <div
                    key={spot.submission_id}
                    style={{
                      minWidth: '150px',
                      maxWidth: '160px',
                      background: 'white',
                      borderRadius: '12px',
                      padding: '12px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      border: `2px solid ${getActivityColor(spot.category)}`,
                      flexShrink: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px'
                    }}
                  >
                    <div style={{
                      fontSize: '28px',
                      textAlign: 'center',
                      lineHeight: 1
                    }}>
                      {categoryEmoji}
                    </div>
                    <h4 style={{
                      fontSize: '13px',
                      fontWeight: '700',
                      color: '#333',
                      margin: 0,
                      textAlign: 'center',
                      lineHeight: 1.2
                    }}>
                      {spot.name}
                    </h4>
                    <p style={{
                      fontSize: '11px',
                      color: '#666',
                      margin: 0,
                      textAlign: 'center',
                      lineHeight: 1.3,
                      flex: 1
                    }}>
                      {spot.description}
                    </p>
                    {spot.maps_link && (
                      <a
                        href={spot.maps_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'block',
                          textAlign: 'center',
                          fontSize: '10px',
                          fontWeight: '700',
                          color: getActivityColor(spot.category),
                          textDecoration: 'none',
                          padding: '4px 0'
                        }}
                      >
                        📍 Maps
                      </a>
                    )}
                    <div style={{
                      fontSize: '9px',
                      color: '#B8A082',
                      textAlign: 'center',
                      letterSpacing: '0.3px'
                    }}>
                      by {spot.submitter_name}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </>
      )}

      {/* Separator Line between Activities and Challenges */}
      <div style={{
        height: '1px',
        background: 'rgba(184, 160, 130, 0.3)',
        margin: '12px 20px 20px 20px'
      }}></div>

      {/* Challenge Carousel */}
      <div className="custom-challenge-container" style={{margin: '0 20px 20px 20px'}}>
        <h3 style={{fontSize: '12px', color: '#B8A082', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '16px', textAlign: 'center'}}>{t('mainPage.chooseChallenge')}</h3>
        {availableChallenges.length > 0 ? (
          <div
            className="custom-challenges-carousel"
            style={{
              display: 'flex',
              overflowX: 'auto',
              scrollBehavior: 'smooth',
              gap: '12px',
              paddingBottom: '8px',
              scrollbarWidth: 'thin',
              scrollbarColor: '#B8A082 #f0f0f0'
            }}
            onMouseEnter={() => setIsChallengeCarouselPaused(true)}
            onMouseLeave={() => setIsChallengeCarouselPaused(false)}
            onTouchStart={() => setIsChallengeCarouselPaused(true)}
            onTouchEnd={() => setIsChallengeCarouselPaused(false)}
          >
            {availableChallenges.map((challenge, index) => (
              <div key={challenge.id} className="custom-challenge-card" style={{
                background: 'white',
                borderRadius: '16px',
                padding: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                textAlign: 'center',
                width: '180px',
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column'
              }}>
                <h4 style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#333',
                  margin: '0 0 8px 0',
                  lineHeight: '1.2',
                  height: '32px', // Altura fija para exactamente 2 líneas (14px * 1.2 * 2 ≈ 33px)
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center'
                }}>{challenge.title}</h4>
                {/* Sprint 3 §A — difficulty as quiet info, no traffic-light, no points */}
                <div style={{
                  background: '#ede4d3',
                  color: '#8B7355',
                  padding: '3px 8px',
                  borderRadius: '8px',
                  fontSize: '9px',
                  fontWeight: '600',
                  letterSpacing: '0.3px',
                  display: 'inline-block',
                  marginBottom: '8px'
                }}>{t(`mainPage.difficulty.${challenge.difficulty.toLowerCase()}`).toUpperCase()}</div>
                <p style={{fontSize: '11px', color: '#666', lineHeight: '1.3', margin: '8px 0', textAlign: 'left'}}>{challenge.description}</p>
                <button
                  style={{
                    background: '#C4714A',
                    color: 'white',
                    border: 'none',
                    padding: '8px 12px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: '600',
                    letterSpacing: '0.3px',
                    cursor: 'pointer',
                    width: '100%',
                    marginTop: 'auto' // stick to the bottom so buttons align across cards
                  }}
                  onClick={() => {
                    handleAcceptChallenge(challenge)
                  }}
                >
                  {t('mainPage.challengeAccept')}
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '40px 20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <p style={{fontSize: '16px', color: '#B8A082', margin: '0 0 8px 0'}}>🎉 All challenges completed!</p>
            <p style={{fontSize: '14px', color: '#666', margin: '0'}}>You're a true travel explorer!</p>
          </div>
        )}
      </div>

      {/* Separator Line */}
      <div style={{
        height: '1px',
        background: 'rgba(184, 160, 130, 0.2)',
        margin: '8px 20px'
      }}></div>

      {/* Quick Actions - Main Pills — superseded by the fixed bottom bar
          (Settings · Quick actions · Save). Hidden; navigation lives there now. */}
      <div className="custom-quick-actions" style={{ display: 'none' }}>
        <div style={{
          display: 'flex',
          gap: '8px',
          justifyContent: 'center'
        }}>
          <button 
            style={{
              background: '#C4714A',
              color: 'white',
              border: 'none',
              padding: '12px 16px',
              borderRadius: '25px',
              fontSize: '12px',
              fontWeight: '600',
              letterSpacing: '0.3px',
              cursor: 'pointer',
              flex: '1',
              textAlign: 'center',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 'fit-content'
            }}
            onClick={() => onNavigate('notes')}
          >
            {t('menu.notes')}
          </button>
          <button
            style={{
              background: '#C4714A',
              color: 'white',
              border: 'none',
              padding: '12px 16px',
              borderRadius: '25px',
              fontSize: '12px',
              fontWeight: '600',
              letterSpacing: '0.3px',
              cursor: 'pointer',
              flex: '1',
              textAlign: 'center',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 'fit-content'
            }}
            onClick={() => onNavigate('plans')}
          >
            {t('menu.plans')}
          </button>
          <button
            style={{
              background: '#C4714A',
              color: 'white',
              border: 'none',
              padding: '12px 16px',
              borderRadius: '25px',
              fontSize: '12px',
              fontWeight: '600',
              letterSpacing: '0.3px',
              cursor: 'pointer',
              flex: '1',
              textAlign: 'center',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 'fit-content'
            }}
            onClick={() => onNavigate('info')}
          >
            {t('menu.usefulInfo')}
          </button>
          <button
            style={{
              background: '#C4714A',
              color: 'white',
              border: 'none',
              padding: '12px 16px',
              borderRadius: '25px',
              fontSize: '12px',
              fontWeight: '600',
              letterSpacing: '0.3px',
              cursor: 'pointer',
              flex: '1',
              textAlign: 'center',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 'fit-content'
            }}
            onClick={() => onNavigate('checklists')}
          >
            {t('menu.checklists')}
          </button>
        </div>

        {/* Secondary Actions - Summary · Settings · Save trip */}
        <div style={{
          display: 'flex',
          gap: '6px',
          justifyContent: 'center'
        }}>
          <button
            style={{
              background: 'linear-gradient(135deg, #C4714A, #A85B38)',
              color: 'white',
              border: 'none',
              padding: '10px 12px',
              borderRadius: '16px',
              fontSize: '12px',
              fontWeight: '700',
              letterSpacing: '0.3px',
              cursor: 'pointer',
              flex: '1',
              textAlign: 'center',
              whiteSpace: 'nowrap',
              boxShadow: '0 2px 8px rgba(196, 113, 74, 0.35)'
            }}
            onClick={() => onNavigate('summary')}
          >
            {t('menu.summary')}
          </button>
          <button
            style={{
              background: '#2D5A6E',
              color: 'white',
              border: 'none',
              padding: '10px 12px',
              borderRadius: '16px',
              fontSize: '12px',
              fontWeight: '700',
              letterSpacing: '0.3px',
              cursor: 'pointer',
              flex: '1',
              textAlign: 'center',
              whiteSpace: 'nowrap',
              boxShadow: '0 2px 8px rgba(45, 90, 110, 0.30)'
            }}
            onClick={() => onNavigate('settings')}
          >
            {t('menu.settings')}
          </button>
          <button
            style={{
              background: '#8B7355',
              color: 'white',
              border: 'none',
              padding: '10px 12px',
              borderRadius: '16px',
              fontSize: '12px',
              fontWeight: '700',
              letterSpacing: '0.3px',
              cursor: 'pointer',
              flex: '1',
              textAlign: 'center',
              whiteSpace: 'nowrap',
              boxShadow: '0 2px 8px rgba(139, 115, 85, 0.30)'
            }}
            onClick={() => {
              if (isRunning('backup')) return
              runTask({
                id: 'backup', kind: 'backup', label: t('tasks.backupLabel'),
                run: async (onProgress) => {
                  const backup = await autoBackupManager.createBackup(onProgress)
                  // If Drive is connected, upload straight there (fetch, no gesture);
                  // otherwise the completion chip offers share/download.
                  if (googleDrive.isConnected()) {
                    onProgress({ phase: t('tasks.uploadingDrive'), current: 0, total: 0 })
                    try {
                      await googleDrive.uploadBackup(backup, `wayfare-backup-${new Date().toISOString().slice(0, 10)}.json`)
                      autoBackupManager.markBackedUp()
                      return { kind: 'backup', uploaded: true, doneText: t('tasks.backupDrive') }
                    } catch {
                      return { kind: 'backup', backup, doneText: t('tasks.backupReady') }
                    }
                  }
                  return { kind: 'backup', backup, doneText: t('tasks.backupReady') }
                },
              })
            }}
          >
            {t('menu.saveTrip')}
          </button>
        </div>
      </div>

      {/* Separator Line */}
      <div style={{
        height: '1px',
        background: 'rgba(184, 160, 130, 0.2)',
        margin: '8px 20px'
      }}></div>

      {/* Footer Messages */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '12px',
        margin: '0 20px 0px 20px',
        fontSize: '14px',
        color: '#8B7355'
      }}>
        <span>{t('footer.stayHydrated')}</span>
        <span>|</span>
        <span>{t('footer.staySafe')}</span>
        <span>|</span>
        <span>{t('footer.stayAware')}</span>
      </div>

      {/* Separator Line */}
      <div style={{
        height: '1px',
        background: 'rgba(184, 160, 130, 0.2)',
        margin: '8px 20px'
      }}></div>

      {/* Brand Section - 2 Columns */}
      <div style={{
        display: 'flex',
        margin: '16px 20px 20px 20px',
        gap: '16px',
        alignItems: 'flex-start'
      }}>
        {/* Column 1: Description (75%) */}
        <div style={{
          flex: '1',
          fontSize: '12px',
          lineHeight: '1.5',
          color: '#8B7355'
        }}>
          <p style={{margin: '0 0 8px 0',fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px'}}>
            <span style={{ fontFamily: "'Noto Serif', serif", textTransform: 'uppercase', letterSpacing: '1px' }}>WAYFARE</span> by LOS DIAS
            <VersionFooter />
          </p>
          <p style={{margin: '0 0 8px 0'}}>
            {t('footer.description')}
          </p>
          <p style={{margin: '0', fontSize: '11px', opacity: '0.8'}}>
            {t('footer.enhanced')}
          </p>
        </div>

        {/* Column 2: Logo & Brand (25%) */}
        <div style={{
          flex: '0 0 120px',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <img 
            src="./assets/logo.png"
            alt="DIAS Logo"
            style={{
              width: '80px',
              height: '80px',
              borderRadius: '8px',
              marginBottom: '8px',
              objectFit: 'cover'
            }}
          />
          <div style={{
            fontSize: '14px',
            fontWeight: '700',
            color: '#8B7355',
            marginBottom: '4px',
            letterSpacing: '1px'
          }}>
            DIAS
          </div>
          
          <a 
            href="https://www.losdias.xyz" 
            style={{
              fontSize: '10px',
              color: '#B8A082',
              textDecoration: 'none'
            }}
            target="_blank"
            rel="noopener noreferrer"
          >
            www.losdias.xyz
          </a>
           {/* Separator Line */}
      <div style={{
        height: '3px',
        background: 'rgba(184, 160, 130, 0.2)',
        margin: '5px 0px'
      }}></div>

          <a
            href="https://www.losdias.xyz/desafios-diarios"
            style={{
              fontSize: '10px',
              color: '#B8A082',
              textDecoration: 'none'
            }}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('footer.nfcTshirts')}
          </a>
          <a
            href="https://www.losdias.xyz/origen"
            style={{
              fontSize: '10px',
              color: '#B8A082',
              textDecoration: 'none'
            }}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('footer.aboutUs')}
          </a>
          <a
            href="https://www.losdias.xyz/contactus"
            style={{
              fontSize: '10px',
              color: '#B8A082',
              textDecoration: 'none'
            }}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('footer.contactUs')}
          </a>
        </div>
      </div>

      {/* Separator Line */}
      <div style={{
        height: '1px',
        background: 'rgba(184, 160, 130, 0.2)',
        margin: '8px 20px'
      }}></div>

      {/* Copyright Section */}
      <div style={{
        textAlign: 'center',
        padding: '12px 20px 20px 20px',
        fontSize: '10px',
        color: '#8B7355',
        opacity: '0.7'
      }}>
        <p style={{ margin: '0' }}>
          {t('footer.copyright')}
        </p>
      </div>

      {/* Challenge Schedule Modal */}
      {showScheduleModal && pendingChallenge && (() => {
        // Calculate trip duration from dates
        const tripDuration = userData?.endDate && userData?.startDate
          ? Math.ceil((new Date(userData.endDate) - new Date(userData.startDate)) / (1000 * 60 * 60 * 24)) + 1
          : userData?.totalDays || 7;

        console.log('[Challenge Modal] userData:', userData);
        console.log('[Challenge Modal] tripDuration:', tripDuration);
        console.log('[Challenge Modal] startDate:', userData?.startDate);
        console.log('[Challenge Modal] endDate:', userData?.endDate);
        console.log('[Challenge Modal] totalDays:', userData?.totalDays);

        return (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}>
            <div style={{
              background: 'white',
              borderRadius: '20px',
              padding: '30px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
            }}>
              <h2 style={{
                color: '#8B7355',
                fontSize: '24px',
                fontWeight: '700',
                margin: '0 0 20px 0',
                textAlign: 'center'
              }}>{t('challengeModal.title')}</h2>

              <div style={{
                background: '#f9f7f4',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '16px',
                border: '2px solid #B8A082'
              }}>
                <h3 style={{
                  color: '#8B7355',
                  fontSize: '18px',
                  fontWeight: '600',
                  margin: '0 0 8px 0'
                }}>{pendingChallenge.title}</h3>
                <p style={{
                  color: '#666',
                  fontSize: '14px',
                  margin: '0',
                  lineHeight: '1.4'
                }}>{pendingChallenge.description}</p>
              </div>

              {/* Prize Info */}
              <div style={{
                background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                borderRadius: '12px',
                padding: '12px 16px',
                marginBottom: '24px',
                textAlign: 'center'
              }}>
                <p style={{
                  color: '#333',
                  fontSize: '13px',
                  fontWeight: '600',
                  margin: '0',
                  lineHeight: '1.5'
                }}>
                  {t('challengeModal.prizeInfo')}{' '}
                  <a
                    href="https://www.losdias.xyz"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: '#333',
                      textDecoration: 'underline',
                      fontWeight: '700'
                    }}
                  >
                    www.losdias.xyz
                  </a>
                </p>
              </div>

              {/* Day Selection */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#8B7355',
                  marginBottom: '10px'
                }}>{t('challengeModal.whichDay')}</label>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(60px, 1fr))',
                  gap: '8px'
                }}>
                  {Array.from({ length: Math.min(tripDuration, 14) }, (_, i) => i + 1).map(day => (
                  <button
                    key={day}
                    onClick={() => setScheduleDay(day)}
                    style={{
                      padding: '12px',
                      border: '2px solid',
                      borderColor: scheduleDay === day ? '#5B8FA8' : '#ddd',
                      borderRadius: '12px',
                      backgroundColor: scheduleDay === day ? '#5B8FA8' : 'white',
                      color: scheduleDay === day ? 'white' : '#8B7355',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      textAlign: 'center'
                    }}
                  >
                    {t('challengeModal.day', { number: day })}
                  </button>
                ))}
              </div>
            </div>

            {/* Period Selection */}
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                fontSize: '16px',
                fontWeight: '600',
                color: '#8B7355',
                marginBottom: '10px'
              }}>{t('challengeModal.whatTime')}</label>

              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '12px'
              }}>
                {[
                  { key: 'morning', label: t('challengeModal.morning'), desc: t('challengeModal.morningDesc') },
                  { key: 'afternoon', label: t('challengeModal.afternoon'), desc: t('challengeModal.afternoonDesc') }
                ].map(period => {
                  const tripData = Storage.getTripData();
                  const timelineKey = `day${scheduleDay}_${period.key}`;
                  const existingActivities = tripData.timeline?.[timelineKey] || [];
                  const existingCount = existingActivities.length;
                  const isFull = existingCount >= 3;

                  return (
                    <button
                      key={period.key}
                      onClick={() => !isFull && setSchedulePeriod(period.key)}
                      disabled={isFull}
                      style={{
                        padding: '16px',
                        border: '2px solid',
                        borderColor: schedulePeriod === period.key ? '#5B8FA8' : (isFull ? '#ff6b6b' : '#ddd'),
                        borderRadius: '12px',
                        backgroundColor: isFull ? '#ffe6e6' : (schedulePeriod === period.key ? '#5B8FA8' : 'white'),
                        color: isFull ? '#ff6b6b' : (schedulePeriod === period.key ? 'white' : '#8B7355'),
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: isFull ? 'not-allowed' : 'pointer',
                        textAlign: 'center',
                        opacity: isFull ? 0.6 : 1
                      }}
                    >
                      <div>{period.label}</div>
                      <div style={{ fontSize: '11px', marginTop: '4px', opacity: 0.8 }}>
                        {isFull ? t('challengeModal.full', { count: existingCount }) : t('challengeModal.activities', { count: existingCount })}
                      </div>
                      <div style={{ fontSize: '10px', marginTop: '2px', opacity: 0.7 }}>
                        {period.desc}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setShowScheduleModal(false);
                  setPendingChallenge(null);
                }}
                style={{
                  background: 'transparent',
                  color: '#8B7355',
                  border: '2px solid #8B7355',
                  padding: '12px 20px',
                  borderRadius: '25px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {t('challengeModal.cancel')}
              </button>

              <button
                onClick={handleConfirmSchedule}
                style={{
                  background: '#C4714A',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '25px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(184, 160, 130, 0.3)'
                }}
              >
                {t('challengeModal.scheduleButton')}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Day Detail Modal */}
      {showDayDetailModal && selectedDetailDay && (
        <DayDetailModal
          isOpen={showDayDetailModal}
          tripDay={selectedDetailDay}
          userData={userData}
          onClose={() => { setShowDayDetailModal(false); setSelectedDetailDay(null) }}
          onNavigate={onNavigate}
          onDataChanged={() => setTimelineRefreshKey(k => k + 1)}
        />
      )}

      {/* Spacer so page content clears the fixed bottom bar */}
      <div style={{ height: '92px' }} />

      {/* Fixed bottom bar — Settings · Quick actions (center, raised) · Save */}
      {onShowQuickMenu && (
        <div style={{
          position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 1000,
          background: '#ffffff', borderTop: '1px solid #ece5d8',
          boxShadow: '0 -2px 14px rgba(0,0,0,0.08)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-around',
          height: '64px', paddingBottom: 'env(safe-area-inset-bottom, 0px)'
        }}>
          <button onClick={() => onNavigate('settings')}
            style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', background: 'none', border: 'none', color: '#8B7355', cursor: 'pointer' }}>
            <span style={{ fontSize: '22px' }}>⚙️</span>
            <span style={{ fontSize: '10px', fontWeight: 600 }}>{t('bottomNav.settings')}</span>
          </button>

          <div style={{ flex: '0 0 76px', position: 'relative', display: 'flex', justifyContent: 'center' }}>
            <button onClick={onShowQuickMenu} aria-label={t('bottomNav.quick')}
              style={{ position: 'absolute', top: '-46px', width: '62px', height: '62px', borderRadius: '50%', background: 'linear-gradient(135deg, #C4714A, #A85B38)', color: 'white', border: '4px solid #fff', boxShadow: '0 5px 16px rgba(196,113,74,0.45)', fontSize: '26px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              ⚡
            </button>
          </div>

          <button onClick={handleSaveTrip}
            style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', background: 'none', border: 'none', color: '#8B7355', cursor: 'pointer' }}>
            <span style={{ fontSize: '22px' }}>💾</span>
            <span style={{ fontSize: '10px', fontWeight: 600 }}>{t('bottomNav.save')}</span>
          </button>
        </div>
      )}
    </div>
  )
}

// Header Component
function Header({ userData, onMenuToggle, currentView, translatedCityName, tripId }) {
  const displayCity = translatedCityName || userData.destination;
  return (
    <div className="header">
      <div className="header-content">
        <button className="menu-button" onClick={onMenuToggle}>
          ☰
        </button>
        <h1 className="app-title">🧳 <span style={{ fontFamily: "'Noto Serif', serif", textTransform: 'uppercase', letterSpacing: '2px' }}>WAYFARE</span></h1>
        <div className="user-info">
          {tripId && <AutoSaveIndicator tripId={tripId} />}
          <span className="destination-badge">{displayCity}</span>
        </div>
      </div>
      <div className="header-subtitle">
        <span className="current-view">{currentView.toUpperCase()}</span>
      </div>
    </div>
  )
}

// Main Travel App with Proper Flow
function TravelAppProper() {
  const { t, i18n } = useTranslation();
  const [appState, setAppState] = useState('splash') // splash, onboarding, auth, app
  const [userData, setUserData] = useState(null)
  const [currentView, setCurrentView] = useState('home')
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [menuAnimating, setMenuAnimating] = useState(false)
  const [menuVisible, setMenuVisible] = useState(false)
  const [refreshHomeData, setRefreshHomeData] = useState(0)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [currentUser, setCurrentUser] = useState(null)
  const [showPremiumChallengeModal, setShowPremiumChallengeModal] = useState(false)
  const [showPremiumWelcomeModal, setShowPremiumWelcomeModal] = useState(false)
  const [activationResult, setActivationResult] = useState(null)
  const [translatedCityName, setTranslatedCityName] = useState(null)
  const [showQuickMenu, setShowQuickMenu] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [opfsRecoveryBanner, setOpfsRecoveryBanner] = useState(null) // { tripCount }
  const [showPermsOnboarding, setShowPermsOnboarding] = useState(false)

  useEffect(() => {
    const handler = (e) => setOpfsRecoveryBanner({ tripCount: e.detail?.tripCount || 0 });
    window.addEventListener('wayfare-opfs-recovered', handler);
    return () => window.removeEventListener('wayfare-opfs-recovered', handler);
  }, [])

  useEffect(() => {
    if (appState === 'app' && !localStorage.getItem('wayfare_perms_v1')) {
      setShowPermsOnboarding(true)
    }
  }, [appState])

  // Initialize deep link manager and listen for NFC activation events
  useEffect(() => {
    console.log('🔗 Initializing deep link event listeners...');

    // Initialize deep link manager
    deepLinkManager.initialize();

    // Handle authentication required for NFC activation
    const handleAuthRequired = (event) => {
      const { nfcId, message } = event.detail;
      console.log('🔐 Deep link auth required:', nfcId);

      // Store NFC activation as pending and show auth screen
      if (appState !== 'app') {
        alert(message);
        // If not authenticated, the pending activation will be processed after login
      }
    };

    // Handle NFC activation loading state
    const handleActivationLoading = (event) => {
      const { nfcId, message } = event.detail;
      console.log('⏳ NFC activation loading:', nfcId);
      // You could show a loading modal here
    };

    // Handle NFC activation success
    const handleActivationSuccess = (event) => {
      const { type, message, source, expiresAt } = event.detail;
      console.log('✅ NFC activation success:', { type, message, source });

      // Refresh license status
      licenseManager.checkLicenseStatus();

      // Show success modal
      setActivationResult({
        success: true,
        type,
        message,
        source,
        expiresAt
      });
      setShowPremiumWelcomeModal(true);

      // Clean URL parameters
      deepLinkManager.cleanUrl();
    };

    // Handle NFC activation error
    const handleActivationError = (event) => {
      const { error, message } = event.detail;
      console.error('❌ NFC activation error:', error);

      // Show error modal or alert
      setActivationResult({
        success: false,
        error,
        message
      });
      setShowPremiumWelcomeModal(true);

      // Clean URL parameters
      deepLinkManager.cleanUrl();
    };

    // Handle share activation success
    const handleShareSuccess = (event) => {
      const { type, message, sharedBy, expiresAt } = event.detail;
      console.log('🤝 Share activation success:', { type, message, sharedBy });

      // Refresh license status
      licenseManager.checkLicenseStatus();

      // Show success modal
      setActivationResult({
        success: true,
        type,
        message: `${message}\nShared by: ${sharedBy}`,
        expiresAt
      });
      setShowPremiumWelcomeModal(true);

      // Clean URL parameters
      deepLinkManager.cleanUrl();
    };

    // Handle share activation error
    const handleShareError = (event) => {
      const { error, message } = event.detail;
      console.error('❌ Share activation error:', error);

      // Show error modal or alert
      alert(message);

      // Clean URL parameters
      deepLinkManager.cleanUrl();
    };

    // Handle generic deep link errors
    const handleDeepLinkError = (event) => {
      const { message } = event.detail;
      console.error('❌ Deep link error:', message);
      alert(message);
    };

    // Handle navigation to premium section
    const handleNavigateToPremium = () => {
      console.log('💎 Navigating to premium section');
      setShowPremiumChallengeModal(true);
    };

    // Handle download prompt
    const handleDownloadPrompt = () => {
      console.log('📥 Showing download prompt');
      alert('Download the DIAS Travel app to unlock premium features!');
    };

    // Handle NFC Quick Menu for already-premium users
    const handleNfcQuickMenu = () => setShowQuickMenu(true);

    // Add event listeners
    window.addEventListener('nfc-quick-menu', handleNfcQuickMenu);
    window.addEventListener('deep-link-auth-required', handleAuthRequired);
    window.addEventListener('nfc-activation-loading', handleActivationLoading);
    window.addEventListener('nfc-activation-success', handleActivationSuccess);
    window.addEventListener('nfc-activation-error', handleActivationError);
    window.addEventListener('share-activation-success', handleShareSuccess);
    window.addEventListener('share-activation-error', handleShareError);
    window.addEventListener('deep-link-error', handleDeepLinkError);
    window.addEventListener('navigate-to-premium', handleNavigateToPremium);
    window.addEventListener('show-download-prompt', handleDownloadPrompt);

    console.log('✅ Deep link event listeners initialized');

    // Cleanup on unmount
    return () => {
      window.removeEventListener('nfc-quick-menu', handleNfcQuickMenu);
      window.removeEventListener('deep-link-auth-required', handleAuthRequired);
      window.removeEventListener('nfc-activation-loading', handleActivationLoading);
      window.removeEventListener('nfc-activation-success', handleActivationSuccess);
      window.removeEventListener('nfc-activation-error', handleActivationError);
      window.removeEventListener('share-activation-success', handleShareSuccess);
      window.removeEventListener('share-activation-error', handleShareError);
      window.removeEventListener('deep-link-error', handleDeepLinkError);
      window.removeEventListener('navigate-to-premium', handleNavigateToPremium);
      window.removeEventListener('show-download-prompt', handleDownloadPrompt);
    };
  }, [appState, licenseManager])

  // Initialize Persistent Storage to protect data from auto-deletion
  useEffect(() => {
    async function initializePersistentStorage() {
      try {
        console.log('💾 Requesting persistent storage...');
        const result = await persistentStorage.requestPersistence();

        if (result.granted) {
          console.log('✅ Persistent storage granted:', result.message);
        } else {
          console.warn('⚠️ Persistent storage not granted:', result.message);
          // This is normal - browser may not grant persistence
          // Backup system will still work via auto-backup prompts
        }
      } catch (error) {
        console.error('❌ Error requesting persistent storage:', error);
      }
    }

    // Only request after app is loaded and authenticated
    if (appState === 'app') {
      initializePersistentStorage();
    }
  }, [appState])

  const handleSplashComplete = () => {
    // DEV-only auto-login: the auth screen calls PHP endpoints (login.php) that
    // don't exist on the Vite dev server, so login can't succeed on localhost.
    // Seed a valid client-side token so local testing skips the auth screen.
    // import.meta.env.DEV is false in production builds, so Vite strips this block.
    if (import.meta.env.DEV && !authManager.isLoggedIn()) {
      const now = Math.floor(Date.now() / 1000);
      const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
      const payload = btoa(JSON.stringify({ id: 1, email: 'dev@localhost', exp: now + 7 * 24 * 60 * 60, iat: now }));
      localStorage.setItem('auth_token', `${header}.${payload}.${btoa('travel_app_signature')}`);
      localStorage.setItem('auth_method', 'password');
      localStorage.setItem('user_profile', JSON.stringify({ name: 'Dev', email: 'dev@localhost' }));
      console.log('🔓 [DEV] Auto-login bypass active (not present in production builds)');
    }

    // Check authentication first
    const isLoggedIn = authManager.isLoggedIn();
    const user = authManager.getCurrentUser();

    if (!isLoggedIn || !user) {
      setAppState('auth');
      return;
    }

    setIsAuthenticated(true);
    setCurrentUser(user);

    // Scope all trip data to this account
    if (user.email) localStorage.setItem('wayfare_owner', user.email);

    // Check if new user
    const onboardingCompleted = Storage.get('onboardingCompleted', false);
    const settings = Storage.getSettings();

    // NEW FLOW: Skip onboarding, go straight to app
    loadActiveTrip(settings);
    setAppState('app');

    // Startup view is decided by loadActiveTrip(): active trip → Home, none → My Trips.
  }

  const handleAuthenticated = (user) => {
    setIsAuthenticated(true);
    setCurrentUser(user);

    // Scope all trip data to this account (loadActiveTrip below claims/loads it)
    if (user.email) localStorage.setItem('wayfare_owner', user.email);
    else localStorage.removeItem('wayfare_owner');

    // CRITICAL FIX: Clear any old pre-authentication data that might exist
    // This ensures new users start completely fresh
    if (!localStorage.getItem('auth_system_initialized')) {
      console.log('[Auth] First time with auth system - clearing old data');
      localStorage.removeItem('onboardingCompleted');
      localStorage.removeItem('user_settings');
      localStorage.removeItem('trip_data');
      localStorage.removeItem('budget_data');
      localStorage.setItem('auth_system_initialized', 'true');
    }

    // Restore user's saved data if it exists (with proper verification)
    if (user.email && user.id) {
      const userKey = `user_data_${user.email}_${user.id}`;
      const savedUserData = localStorage.getItem(userKey);

      if (savedUserData) {
        try {
          const parsedData = JSON.parse(savedUserData);

          // SECURITY CHECK: Verify this data actually belongs to this user
          if (parsedData.userId === user.id && parsedData.userEmail === user.email) {
            // Restore user's data
            if (parsedData.onboardingCompleted) localStorage.setItem('onboardingCompleted', parsedData.onboardingCompleted);
            if (parsedData.user_settings) localStorage.setItem('user_settings', parsedData.user_settings);
            if (parsedData.trip_data) localStorage.setItem('trip_data', parsedData.trip_data);
            if (parsedData.budget_data) localStorage.setItem('budget_data', parsedData.budget_data);
            if (parsedData.premium_license) localStorage.setItem('premium_license', parsedData.premium_license);

            console.log('✅ User data restored from previous session');
          } else {
            console.warn('🔒 Security: User data verification failed - not restoring data');
          }
        } catch (error) {
          console.error('❌ Failed to restore user data:', error);
        }
      }
    }

    // Store user profile for onboarding pre-fill
    const userProfile = {
      userName: user.name || 'TRAVELER',
      userNationality: user.nationality || '',
      userEmail: user.email || ''
    };

    // After authentication and data restoration, check onboarding
    const onboardingCompleted = Storage.get('onboardingCompleted', false);
    const settings = Storage.getSettings();

    console.log('[Auth] Trip check:', { onboardingCompleted, settings });

    // NEW FLOW: Skip onboarding, go straight to app
    // If user has no trips, MY TRIPS page will show "Create New Trip" button
    loadActiveTrip(settings);
    setAppState('app');

    // Startup view is decided by loadActiveTrip(): active trip → Home, none → My Trips.
  }

  const handleOnboardingComplete = (userData) => {
    // Save to localStorage
    Storage.set('onboardingCompleted', true);
    Storage.set('userName', userData.userName);
    Storage.set('userLocation', userData.destination);
    Storage.set('userCountry', userData.country);

    // Update userData state
    setUserData({
      userName: userData.userName,
      destination: userData.destination,
      location: userData.destination,
      country: userData.country,
      theme: 'auto'
    });

    setAppState('app');
  }

  // Load active trip from IndexedDB on app start
  const loadActiveTrip = async (fallbackSettings) => {
    try {
      const storageManager = (await import('./utils/storageManager.js')).default;
      await storageManager.initialize();

      // One-time: tag any owner-less trips to the current account, then scope.
      await storageManager.claimUntaggedTrips();

      // Recover trips_index if localStorage was cleared but IndexedDB has trips
      await storageManager.ensureTripsIndexSync();

      const activeTrip = await storageManager.getActiveTrip();

      if (activeTrip) {
        // Active trip found in IndexedDB - use it
        const city = activeTrip.destination?.city || 'CITY';
        const country = activeTrip.destination?.country || 'COUNTRY';

        console.log('[TravelAppProper] Loading active trip from IndexedDB:', city, country);

        setUserData({
          ...fallbackSettings,
          tripId: activeTrip.tripId,
          destination: city,
          location: city,
          country: country,
          startDate: activeTrip.dates?.startDate || null,
          endDate: activeTrip.dates?.endDate || null,
          currentDay: activeTrip.dates?.currentDay || 1,
          totalDays: activeTrip.dates?.totalDays || 1,
          userName: activeTrip.traveler?.name || 'TRAVELER',
          nationality: activeTrip.traveler?.nationality || '',
          userNationality: activeTrip.traveler?.nationality || ''
        });

        // We have an active trip → open straight into its main page (Home).
        setCurrentView('home');

        // Load trip data into cache
        try {
          const tripData = await Storage.getTripDataAsync();
          console.log('[TravelAppProper] ✅ Loaded trip data into cache:', {
            notesCount: tripData.notes?.length || 0,
            timelineKeys: Object.keys(tripData.timeline || {})
          });
        } catch (error) {
          console.error('[TravelAppProper] ❌ Failed to load trip data:', error);
        }
      } else {
        // No active trip - use localStorage settings + show My Trips to pick/create one
        console.log('[TravelAppProper] No active trip, using localStorage settings');
        window.__cachedTripData = null;
        setUserData(fallbackSettings);
        setCurrentView('mytrips');
      }
    } catch (error) {
      console.error('[TravelAppProper] Failed to load active trip:', error);
      // Fallback to localStorage settings
      setUserData(fallbackSettings);
    }
  };

  const handleTripChange = async (trip) => {
    if (!trip) {
      // No active trip - reset to defaults
      setUserData({
        ...userData,
        destination: 'CITY',
        location: 'CITY',
        country: 'COUNTRY',
        startDate: null,
        endDate: null,
        currentDay: 1,
        totalDays: 1
      });
      // Clear cache
      window.__cachedTripData = null;
      console.log('[TravelAppProper] No active trip - reset to defaults');
      return;
    }

    // Update userData with new trip info
    const city = trip.destination?.city || trip.destination || 'CITY';
    const country = trip.destination?.country || trip.country || 'COUNTRY';

    setUserData({
      ...userData,
      tripId: trip.tripId,
      destination: city,
      location: city,
      country: country,
      startDate: trip.dates?.startDate || null,
      endDate: trip.dates?.endDate || null,
      currentDay: trip.dates?.currentDay || 1,
      totalDays: trip.dates?.totalDays || 1,
      userName: trip.traveler?.name || 'TRAVELER',
      nationality: trip.traveler?.nationality || '',
      userNationality: trip.traveler?.nationality || ''
    });

    console.log('[TravelAppProper] Trip changed to:', city, country);

    // Load trip data into cache for new trip
    try {
      const tripData = await Storage.getTripDataAsync();
      console.log('[TravelAppProper] ✅ Loaded trip data for new trip:', {
        notesCount: tripData.notes?.length || 0,
        timelineKeys: Object.keys(tripData.timeline || {})
      });
    } catch (error) {
      console.error('[TravelAppProper] ❌ Failed to load trip data:', error);
    }

    // Trigger refresh of home data
    setRefreshHomeData(prev => prev + 1);
  }

  const handleLogout = () => {
    // Save current user's data before logout
    if (currentUser && currentUser.email && currentUser.id) {
      const userDataToSave = {
        onboardingCompleted: localStorage.getItem('onboardingCompleted'),
        user_settings: localStorage.getItem('user_settings'),
        trip_data: localStorage.getItem('trip_data'),
        budget_data: localStorage.getItem('budget_data'),
        premium_license: localStorage.getItem('premium_license'),
        userId: currentUser.id,  // Store user ID for verification
        userEmail: currentUser.email
      };

      // Store user-specific data using email + ID as key (more secure)
      const userKey = `user_data_${currentUser.email}_${currentUser.id}`;
      localStorage.setItem(userKey, JSON.stringify(userDataToSave));
    }

    // Clear authentication (session data only)
    authManager.logout();
    licenseManager.reset(); // drop in-memory premium state so the next user re-checks
    localStorage.removeItem('wayfare_owner'); // stop scoping trips to the logged-out user
    setIsAuthenticated(false);
    setCurrentUser(null);

    // Clear current session data (not user's personal data)
    localStorage.removeItem('onboardingCompleted');
    localStorage.removeItem('user_settings');
    localStorage.removeItem('trip_data');
    localStorage.removeItem('budget_data');
    localStorage.removeItem('user_pin');
    localStorage.removeItem('premium_license');

    // Reset app state
    setUserData(null);
    setAppState('auth');

    console.log('👋 Logout complete - user data saved for next login');
  }

  const handleNavigate = (view, options = {}) => {
    // Redirect legacy views to unified containers
    if (view === 'lodging') { view = 'info'; options = { ...options, tab: 'lodging' } }
    if (view === 'luggage' || view === 'groceries' || view === 'gifts') {
      options = { ...options, initialTab: view }; view = 'checklists'
    }
    setCurrentView(view)
    setMenuAnimating(false)
    setIsMenuOpen(false)
    setTimeout(() => {
      setMenuVisible(false)
    }, 600)

    // Refresh home data when navigating to home
    if (view === 'home') {
      setRefreshHomeData(prev => prev + 1)
    }

    // Store navigation options for components that need them
    if (options && Object.keys(options).length > 0) {
      window.navigationOptions = options
    } else {
      delete window.navigationOptions
    }
  }

  const handleMenuToggle = () => {
    // console.log('Menu toggle clicked, current state:', isMenuOpen, 'menuAnimating:', menuAnimating)
    if (!isMenuOpen) {
      setMenuVisible(true)
      setIsMenuOpen(true)
      setTimeout(() => {
        // console.log('Setting menuAnimating to true')
        setMenuAnimating(true)
      }, 50) // Start animations after menu appears
    } else {
      // console.log('Setting menuAnimating to false')
      setMenuAnimating(false)
      setIsMenuOpen(false)
      setTimeout(() => {
        setMenuVisible(false)
      }, 600) // Hide menu after animations complete
    }
  }

  // Render splash screen
  if (appState === 'splash') {
    return <Splash onComplete={handleSplashComplete} />
  }

  // Render authentication
  if (appState === 'auth') {
    return <AuthScreen onAuthenticated={handleAuthenticated} />
  }

  // Render main app (onboarding removed - now use MY TRIPS page for setup)
  return (
    <div className="travel-app">
      {/* Sprint 1 — background proximity reveal + soft whisper (active trip only) */}
      <ProximityLayer hasActiveTrip={!!userData?.tripId} tripVersion={userData?.tripId || 0} />
      {/* Sprint 4A — unlock the city postcard the moment the last gem is found */}
      <PostcardLayer hasActiveTrip={!!userData?.tripId} />
      {/* Sprint 4C — completion chip for background tasks (backup / PDF) */}
      <BackgroundTaskLayer />
      {/* Sprint 3 — inbound knowledge gifts (offline deep link) */}
      <GiftReceivedLayer />
      {/* Cloud backup nudge while travelling (active trip + new content, max 1/day) */}
      <CloudBackupNudge hasActiveTrip={!!userData?.tripId} />
      {/* DEV-only tier switch (localhost testing). Vite strips this in production. */}
      {import.meta.env.DEV && (() => {
        const tier = localStorage.getItem('dev_tier') === 'free' ? 'free' : 'premium'
        return (
          <button
            onClick={() => {
              localStorage.setItem('dev_tier', tier === 'free' ? 'premium' : 'free')
              location.reload()
            }}
            title="DEV only — toggle free/premium tier for testing"
            style={{
              position: 'fixed', bottom: '10px', right: '10px', zIndex: 100000,
              background: tier === 'free' ? '#6b7280' : '#b8860b', color: '#fff',
              border: 'none', borderRadius: '8px', padding: '6px 10px',
              fontSize: '11px', fontWeight: 700, opacity: 0.75, cursor: 'pointer',
              boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
            }}
          >
            DEV: {tier === 'free' ? '🆓 FREE' : '⭐ PREMIUM'}
          </button>
        )
      })()}
      {currentView !== 'home' && currentView !== 'notes' && currentView !== 'checklists' && currentView !== 'info' && currentView !== 'plans' && currentView !== 'explore' && currentView !== 'onboarding' && currentView !== 'summary' && currentView !== 'mytrips' && currentView !== 'profile' && currentView !== 'atlas' && currentView !== 'settings' && (
        <Header
          userData={userData}
          currentView={currentView}
          onMenuToggle={handleMenuToggle}
          translatedCityName={translatedCityName}
          tripId={userData?.tripId}
        />
      )}
      
      {menuVisible && (
        <div className="custom-fullscreen-nav-menu" style={{
          position: 'fixed',
          top: '0',
          left: '0',
          width: '100vw',
          height: '100vh',
          background: 'linear-gradient(135deg, #f5f1eb 0%, #ede4d3 100%)',
          zIndex: '9999',
          transform: 'translateX(-100%)',
          animation: isMenuOpen ? 'slideInFromLeft 0.3s ease-out forwards' : 'slideOutToLeft 0.3s ease-out forwards',
          padding: '80px 40px 40px 40px',
          overflow: 'auto'
        }}>
          
          {/* Close Button - same size and position as hamburger */}
          <button className="custom-nav-close-btn" style={{
            position: 'absolute',
            top: '20px',
            left: '20px',
            background: '#C4714A',
            color: 'white',
            border: 'none',
            width: '44px',
            height: '44px',
            borderRadius: '12px',
            fontSize: '24px',
            fontWeight: 'bold',
            cursor: 'pointer',
            zIndex: '10000',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
          }} onClick={() => handleMenuToggle()}>✕</button>

          {/* Menu Items with staggered animation */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            marginTop: '60px'
          }}>
            {/* My Trips - First item */}
            <div style={{
              opacity: isMenuOpen ? (menuAnimating ? 1 : 0) : 0,
              transform: isMenuOpen ? (menuAnimating ? 'translateX(0)' : 'translateX(-50px)') : 'translateX(-50px)',
              transition: 'opacity 0.4s ease-out 0.1s, transform 0.4s ease-out 0.1s'
            }}>
              <div style={{
                fontSize: '32px',
                fontWeight: '900',
                color: '#2D5A6E',
                letterSpacing: '-1px',
                marginBottom: '8px',
                textTransform: 'uppercase',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }} onClick={() => handleNavigate('mytrips')}>
                My Trips
              </div>
            </div>


            {/* Atlas Section */}
            <div style={{
              opacity: isMenuOpen ? (menuAnimating ? 1 : 0) : 0,
              transform: isMenuOpen ? (menuAnimating ? 'translateX(0)' : 'translateX(-50px)') : 'translateX(-50px)',
              transition: 'opacity 0.4s ease-out 0.17s, transform 0.4s ease-out 0.17s'
            }}>
              <div style={{
                fontSize: '32px',
                fontWeight: '900',
                color: '#2D5A6E',
                letterSpacing: '-1px',
                marginBottom: '8px',
                textTransform: 'uppercase',
                cursor: 'pointer'
              }} onClick={() => handleNavigate('atlas')}>{t('menu.atlas')}</div>
            </div>

            {/* Explore Section */}
            <div style={{
              opacity: isMenuOpen ? (menuAnimating ? 1 : 0) : 0,
              transform: isMenuOpen ? (menuAnimating ? 'translateX(0)' : 'translateX(-50px)') : 'translateX(-50px)',
              transition: 'opacity 0.4s ease-out 0.2s, transform 0.4s ease-out 0.2s'
            }}>
              <div style={{
                fontSize: '32px',
                fontWeight: '900',
                color: '#2D5A6E',
                letterSpacing: '-1px',
                marginBottom: '8px',
                textTransform: 'uppercase',
                cursor: 'pointer'
              }} onClick={() => handleNavigate('explore')}>{t('menu.explore')}</div>
            </div>

            {/* Notes Section */}
            <div style={{
              opacity: isMenuOpen ? (menuAnimating ? 1 : 0) : 0,
              transform: isMenuOpen ? (menuAnimating ? 'translateX(0)' : 'translateX(-50px)') : 'translateX(-50px)',
              transition: 'opacity 0.4s ease-out 0.2s, transform 0.4s ease-out 0.2s'
            }}>
              <div style={{
                fontSize: '32px',
                fontWeight: '900',
                color: '#2D5A6E',
                letterSpacing: '-1px',
                marginBottom: '8px',
                textTransform: 'uppercase',
                cursor: 'pointer'
              }} onClick={() => handleNavigate('notes')}>{t('menu.notes')}</div>
            </div>

            <div style={{
              opacity: isMenuOpen ? (menuAnimating ? 1 : 0) : 0,
              transform: isMenuOpen ? (menuAnimating ? 'translateX(0)' : 'translateX(-50px)') : 'translateX(-50px)',
              transition: 'opacity 0.4s ease-out 0.3s, transform 0.4s ease-out 0.3s'
            }}>
              <div style={{
                fontSize: '32px',
                fontWeight: '900',
                color: '#2D5A6E',
                letterSpacing: '-1px',
                marginBottom: '8px',
                textTransform: 'uppercase',
                cursor: 'pointer'
              }} onClick={() => handleNavigate('plans')}>{t('menu.plans')}</div>
            </div>

            <div style={{
              opacity: isMenuOpen ? (menuAnimating ? 1 : 0) : 0,
              transform: isMenuOpen ? (menuAnimating ? 'translateX(0)' : 'translateX(-50px)') : 'translateX(-50px)',
              transition: 'opacity 0.4s ease-out 0.35s, transform 0.4s ease-out 0.35s'
            }}>
              <div style={{
                fontSize: '32px',
                fontWeight: '900',
                color: '#2D5A6E',
                letterSpacing: '-1px',
                marginBottom: '8px',
                textTransform: 'uppercase',
                cursor: 'pointer'
              }} onClick={() => handleNavigate('info')}>{t('menu.usefulInfo')}</div>
            </div>

            <div style={{
              opacity: isMenuOpen ? (menuAnimating ? 1 : 0) : 0,
              transform: isMenuOpen ? (menuAnimating ? 'translateX(0)' : 'translateX(-50px)') : 'translateX(-50px)',
              transition: 'opacity 0.4s ease-out 0.4s, transform 0.4s ease-out 0.4s'
            }}>
              <div style={{
                fontSize: '32px',
                fontWeight: '900',
                color: '#2D5A6E',
                letterSpacing: '-1px',
                marginBottom: '8px',
                textTransform: 'uppercase',
                cursor: 'pointer'
              }} onClick={() => handleNavigate('checklists')}>{t('menu.checklists')}</div>
            </div>

            <div style={{
              opacity: isMenuOpen ? (menuAnimating ? 1 : 0) : 0,
              transform: isMenuOpen ? (menuAnimating ? 'translateX(0)' : 'translateX(-50px)') : 'translateX(-50px)',
              transition: 'opacity 0.4s ease-out 0.45s, transform 0.4s ease-out 0.45s'
            }}>
              <div style={{
                fontSize: '32px',
                fontWeight: '900',
                color: '#2D5A6E',
                letterSpacing: '-1px',
                marginBottom: '8px',
                textTransform: 'uppercase',
                cursor: 'pointer'
              }} onClick={() => handleNavigate('summary')}>{t('menu.summary')}</div>
            </div>

            {/* Language Selector */}
            <div style={{
              opacity: isMenuOpen ? (menuAnimating ? 1 : 0) : 0,
              transform: isMenuOpen ? (menuAnimating ? 'translateX(0)' : 'translateX(-50px)') : 'translateX(-50px)',
              transition: 'opacity 0.4s ease-out 0.5s, transform 0.4s ease-out 0.5s',
              marginTop: '20px',
              borderTop: '1px solid rgba(139, 115, 85, 0.3)',
              paddingTop: '20px'
            }}>
              <div style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#8B7355',
                marginBottom: '12px'
              }}>{t('menu.language')}</div>
              <div style={{
                display: 'flex',
                gap: '10px',
                justifyContent: 'flex-start'
              }}>
                {['en', 'es', 'fr'].map(lang => (
                  <button
                    key={lang}
                    onClick={() => i18n.changeLanguage(lang)}
                    style={{
                      padding: '8px 16px',
                      border: '2px solid',
                      borderColor: i18n.language === lang ? '#5B8FA8' : '#ddd',
                      borderRadius: '8px',
                      backgroundColor: i18n.language === lang ? '#5B8FA8' : 'white',
                      color: i18n.language === lang ? 'white' : '#8B7355',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      textTransform: 'uppercase'
                    }}
                  >
                    {lang}
                  </button>
                ))}
              </div>
            </div>

            {/* Settings Button */}
            <div style={{
              opacity: isMenuOpen ? (menuAnimating ? 1 : 0) : 0,
              transform: isMenuOpen ? (menuAnimating ? 'translateX(0)' : 'translateX(-50px)') : 'translateX(-50px)',
              transition: 'opacity 0.4s ease-out 0.5s, transform 0.4s ease-out 0.5s',
              marginTop: '16px'
            }}>
              <div style={{
                fontSize: '24px',
                fontWeight: '700',
                color: '#8B7355',
                letterSpacing: '-1px',
                textTransform: 'uppercase',
                cursor: 'pointer'
              }} onClick={() => handleNavigate('settings')}>{t('menu.settings')}</div>
            </div>

            {/* Logout Button */}
            <div style={{
              opacity: isMenuOpen ? (menuAnimating ? 1 : 0) : 0,
              transform: isMenuOpen ? (menuAnimating ? 'translateX(0)' : 'translateX(-50px)') : 'translateX(-50px)',
              transition: 'opacity 0.4s ease-out 0.55s, transform 0.4s ease-out 0.55s',
              marginTop: '16px'
            }}>
              <div style={{
                fontSize: '24px',
                fontWeight: '700',
                color: '#B8A082',
                letterSpacing: '-1px',
                textTransform: 'uppercase',
                cursor: 'pointer',
                opacity: '0.8'
              }} onClick={handleLogout}>{t('menu.logout')}</div>
            </div>
          </div>
        </div>
      )}

      <div className="main-content">
        {currentView === 'home' && (
          <Home
            userData={userData}
            onNavigate={handleNavigate}
            onMenuToggle={handleMenuToggle}
            onChallengeSelect={(challenge) => {/* console.log('Challenge selected:', challenge) */}}
            refreshData={refreshHomeData}
            onShowPremiumChallengeModal={setShowPremiumChallengeModal}
            translatedCityName={translatedCityName}
            onCityNameTranslated={setTranslatedCityName}
            onShowQuickMenu={() => setShowQuickMenu(true)}
          />
        )}

        {currentView === 'atlas' && (
          <AtlasView onNavigate={handleNavigate} />
        )}

        {currentView === 'notes' && (
          <Notes
            userData={userData}
            onNavigate={handleNavigate}
          />
        )}

        {currentView === 'checklists' && (
          <Checklists
            userData={userData}
            onNavigate={handleNavigate}
          />
        )}

        {currentView === 'info' && (
          <UsefulInfo 
            userData={userData}
            onNavigate={handleNavigate}
          />
        )}
        
        {currentView === 'plans' && (
          <Plans
            userData={userData}
            onNavigate={handleNavigate}
            onCityNameTranslated={setTranslatedCityName}
          />
        )}

        {currentView === 'explore' && (
          <Explore
            onNavigate={handleNavigate}
          />
        )}

        {currentView === 'summary' && (
          <TripSummary
            userData={userData}
            onNavigate={handleNavigate}
          />
        )}

        {currentView === 'mytrips' && (
          <MyTrips
            userData={userData}
            onNavigate={handleNavigate}
            onTripChange={handleTripChange}
          />
        )}

        {currentView === 'settings' && (
          <Settings
            userData={userData}
            onNavigate={handleNavigate}
          />
        )}

        {currentView !== 'home' && currentView !== 'notes' && currentView !== 'checklists' && currentView !== 'info' && currentView !== 'plans' && currentView !== 'onboarding' && currentView !== 'explore' && currentView !== 'summary' && currentView !== 'mytrips' && currentView !== 'settings' && currentView !== 'profile' && currentView !== 'atlas' && (
          <div className="coming-soon">
            <h2>
              {currentView === 'challenges' && '🎯 Challenges'}
              {currentView === 'gallery' && '📸 Photo Gallery'}
              {currentView === 'timeline' && '📅 Timeline'}
              {currentView === 'menu' && '📱 Menu'}
            </h2>
            <p>This feature is coming soon!</p>
            <button onClick={() => handleNavigate('home')}>← Back to Home</button>
          </div>
        )}
      </div>

      {/* Quick Menu Bottom Sheet */}
      <QuickMenu
        isOpen={showQuickMenu}
        onClose={() => { setShowQuickMenu(false); setRefreshHomeData(prev => prev + 1) }}
        onNavigate={handleNavigate}
        onGiftPremium={async () => {
          // Only premium users can gift a 7-day trial. Free users are sent to
          // the site to get Premium first.
          const isPremium = await licenseManager.hasFeatureAccess('multiple_trips');
          if (isPremium) {
            setShowShareModal(true);
          } else {
            window.open('https://www.losdias.xyz', '_blank', 'noopener,noreferrer');
          }
        }}
        userData={userData}
      />

      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        userInfo={currentUser}
      />

      {/* Premium Challenge Modal */}
      {showPremiumChallengeModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #B8A082 0%, #8B7355 100%)',
            borderRadius: '24px',
            padding: '32px',
            maxWidth: '400px',
            width: '100%',
            textAlign: 'center',
            color: 'white',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            border: '1px solid rgba(255,255,255,0.2)'
          }}>
            {/* Header */}
            <div style={{ marginBottom: '24px' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏆</div>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '700',
                margin: '0 0 8px 0',
                color: '#FFF'
              }}>
                Premium Challenge Prizes
              </h2>
              <p style={{
                fontSize: '14px',
                color: 'rgba(255,255,255,0.8)',
                margin: '0'
              }}>
                Unlock NFC Premium to win amazing prizes!
              </p>
            </div>

            {/* Prize Benefits */}
            <div style={{
              background: 'rgba(255,255,255,0.1)',
              borderRadius: '16px',
              padding: '20px',
              marginBottom: '24px',
              textAlign: 'left'
            }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                margin: '0 0 16px 0',
                color: '#FFF',
                textAlign: 'center'
              }}>
                🎁 What You Can Win:
              </h3>

              <div style={{ fontSize: '14px', lineHeight: '1.6', color: 'rgba(255,255,255,0.9)' }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                  <span style={{ marginRight: '12px' }}>🧳</span>
                  <span>Exclusive DIAS Travel Gear</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                  <span style={{ marginRight: '12px' }}>🎽</span>
                  <span>Limited Edition DIAS T-Shirts</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                  <span style={{ marginRight: '12px' }}>🎫</span>
                  <span>Travel Experience Vouchers</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                  <span style={{ marginRight: '12px' }}>📱</span>
                  <span>Premium Tech Accessories</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ marginRight: '12px' }}>🌟</span>
                  <span>Special Recognition Badges</span>
                </div>
              </div>
            </div>

            {/* How to Win */}
            <div style={{
              background: 'rgba(255,255,255,0.15)',
              borderRadius: '16px',
              padding: '16px',
              marginBottom: '24px',
              border: '1px solid rgba(255,255,255,0.3)'
            }}>
              <p style={{
                fontSize: '13px',
                margin: '0',
                color: 'rgba(255,255,255,0.9)',
                lineHeight: '1.5'
              }}>
                <strong style={{ color: '#FFF' }}>How to Win:</strong><br/>
                Complete challenges, share on Instagram with your DIAS t-shirt, and get selected by our team for amazing prizes!
              </p>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => setShowPremiumChallengeModal(false)}
                style={{
                  background: 'transparent',
                  color: 'rgba(255,255,255,0.8)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  padding: '12px 20px',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                Maybe Later
              </button>
              <button
                onClick={async () => {
                  setShowPremiumChallengeModal(false);
                  // Trigger NFC upgrade simulation
                  const result = await licenseManager.simulateNFCUpgrade();
                  if (result.success) {
                    setActivationResult(result);
                    setShowPremiumWelcomeModal(true);
                  } else {
                    alert('❌ Upgrade failed. Please try again.');
                  }
                }}
                style={{
                  background: 'linear-gradient(135deg, #FFF 0%, #F0F0F0 100%)',
                  color: '#8B7355',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 12px rgba(255,255,255,0.3)'
                }}
              >
                🔓 Unlock with NFC
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Premium Welcome Modal */}
      {showPremiumWelcomeModal && activationResult && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001,
          padding: '20px'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #B8A082 0%, #8B7355 100%)',
            borderRadius: '24px',
            padding: '32px',
            maxWidth: '450px',
            width: '100%',
            textAlign: 'center',
            color: 'white',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            border: '1px solid rgba(255,255,255,0.2)'
          }}>
            {/* Success Header */}
            <div style={{ marginBottom: '24px' }}>
              <div style={{ fontSize: '64px', marginBottom: '16px' }}>
                {activationResult.type === 'nfc_permanent'
                  ? '🏆'
                  : activationResult.type === 'friend_shared'
                  ? '🤝'
                  : '🎁'}
              </div>
              <h2 style={{
                fontSize: '26px',
                fontWeight: '700',
                margin: '0 0 8px 0',
                color: '#FFF'
              }}>
                {activationResult.type === 'nfc_permanent'
                  ? 'Premium Activated!'
                  : activationResult.type === 'friend_shared'
                  ? 'Friend Premium Access!'
                  : '7-Day Trial Activated!'}
              </h2>
              <p style={{
                fontSize: '16px',
                color: 'rgba(255,255,255,0.9)',
                margin: '0'
              }}>
                {activationResult.message}
              </p>
            </div>

            {/* Premium Features Unlocked */}
            <div style={{
              background: 'rgba(255,255,255,0.15)',
              borderRadius: '16px',
              padding: '20px',
              marginBottom: '24px',
              textAlign: 'left'
            }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                margin: '0 0 16px 0',
                color: '#FFF',
                textAlign: 'center'
              }}>
                ✨ Features Unlocked:
              </h3>

              <div style={{ fontSize: '14px', lineHeight: '1.6', color: 'rgba(255,255,255,0.9)' }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ marginRight: '12px' }}>📄</span>
                  <span>Document Storage</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ marginRight: '12px' }}>ℹ️</span>
                  <span>Travel Information Hub</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ marginRight: '12px' }}>🏆</span>
                  <span>Prize Challenge Participation</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ marginRight: '12px' }}>🧳</span>
                  <span>Advanced Features & More!</span>
                </div>
              </div>
            </div>

            {/* Friend Sharing Info (only for friend-shared users) */}
            {activationResult.type === 'friend_shared' && (
              <div style={{
                background: 'rgba(255,255,255,0.2)',
                borderRadius: '16px',
                padding: '20px',
                marginBottom: '24px'
              }}>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  margin: '0 0 12px 0',
                  color: '#FFD700'
                }}>
                  🤝 Shared by a Friend!
                </h3>
                <p style={{
                  fontSize: '13px',
                  margin: '0',
                  color: 'rgba(255,255,255,0.9)',
                  lineHeight: '1.5'
                }}>
                  A premium user has shared their access with you! You now have <strong>7 days of premium features</strong>. {activationResult.sharedBy && `Thank ${activationResult.sharedBy} for sharing!`}
                </p>
                <p style={{
                  fontSize: '12px',
                  margin: '8px 0 0 0',
                  color: 'rgba(255,255,255,0.7)',
                  lineHeight: '1.4'
                }}>
                  Want permanent access? Get your own premium t-shirt with NFC to unlock forever and share with your friends too!
                </p>
              </div>
            )}

            {/* Sharing Info (only for permanent users) */}
            {activationResult.type === 'nfc_permanent' && (
              <div style={{
                background: 'rgba(255,255,255,0.2)',
                borderRadius: '16px',
                padding: '20px',
                marginBottom: '24px'
              }}>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  margin: '0 0 12px 0',
                  color: '#FFD700'
                }}>
                  🤝 Share the Experience!
                </h3>
                <p style={{
                  fontSize: '13px',
                  margin: '0',
                  color: 'rgba(255,255,255,0.9)',
                  lineHeight: '1.5'
                }}>
                  Your NFC can now give friends <strong>7-day premium trials!</strong> Let them scan your t-shirt to try all premium features. After their trial ends, they'll need to wait 3 days before another trial.
                </p>
              </div>
            )}

            {/* Action Button */}
            <div style={{ textAlign: 'center' }}>
              <button
                onClick={() => {
                  setShowPremiumWelcomeModal(false);
                  setActivationResult(null);
                  // Refresh the page to update premium status
                  window.location.reload();
                }}
                style={{
                  background: 'linear-gradient(135deg, #FFF 0%, #F0F0F0 100%)',
                  color: '#8B7355',
                  border: 'none',
                  padding: '14px 32px',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 12px rgba(255,255,255,0.3)'
                }}
              >
                🚀 Start Exploring Premium!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* OPFS data-recovery banner */}
      {opfsRecoveryBanner && (
        <div style={{
          position: 'fixed', bottom: '80px', left: '50%', transform: 'translateX(-50%)',
          background: '#1b5e20', color: 'white', padding: '0.9rem 1.5rem',
          borderRadius: '14px', boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          zIndex: 9999, display: 'flex', alignItems: 'center', gap: '0.75rem',
          fontSize: '0.9rem', fontWeight: '600', maxWidth: '92vw',
        }}>
          <span style={{ fontSize: '1.2rem' }}>🔒</span>
          <span>
            {opfsRecoveryBanner.tripCount === 1
              ? 'Viaje recuperado automáticamente'
              : `${opfsRecoveryBanner.tripCount} viajes recuperados automáticamente`}
            {' '}— todo guardado.
          </span>
          <button
            onClick={() => setOpfsRecoveryBanner(null)}
            style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', borderRadius: '8px', padding: '0.3rem 0.6rem', cursor: 'pointer', fontWeight: '700', marginLeft: '0.25rem' }}
          >✕</button>
        </div>
      )}

      {/* PWA Install Prompt */}
      <InstallPrompt />

      {/* App Update Prompt */}
      <AppUpdatePrompt />

      {/* Backup Prompt - Auto-backup reminder every 7 days */}
      <BackupPrompt />

      {showPermsOnboarding && (
        <PermissionsOnboarding onDone={() => setShowPermsOnboarding(false)} />
      )}
    </div>
  )
}

export default TravelAppProper